<template>
  <div class="advanced-insights container-fluid py-4">
    <!-- Premium Header -->
    <div class="insights-header" style="padding-bottom: 0!important;">
      <div class="d-flex justify-content-between align-items-start flex-wrap gap-3">
        <div>
          <div class="d-flex align-items-center gap-2 mb-2">
            <!-- <div class="header-icon">
              <AppIcon name="bi-graph-up-arrow" class="bi bi-graph-up-arrow" />
            </div> -->
            <h1 class="h3 mb-0 fw-bold">{{ $t('advanced_insights') }}</h1>
          </div>
        </div>
        <div class="d-flex gap-2 flex-wrap align-items-center">
          <!-- Dashboard Presets Dropdown -->
          <!-- <div class="dropdown">
            <button class="btn btn-outline-secondary btn-sm dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
              <AppIcon name="bi-grid-3x3-gap" class="bi bi-grid-3x3-gap me-1" />
              {{ $t('dashboard_presets') }}
            </button>
          <ul class="dropdown-menu dropdown-menu-end">
              <li><h6 class="dropdown-header">{{ $t('quick_start') }}</h6></li>
              <li><a class="dropdown-item" href="#" @click.prevent="loadPreset('sales')"><AppIcon name="bi-receipt" class="bi bi-receipt me-2 text-success" />{{ $t('sales_dashboard') }}</a></li>
              <li><a class="dropdown-item" href="#" @click.prevent="loadPreset('inventory')"><AppIcon name="bi-box-seam" class="bi bi-box-seam me-2 text-primary" />{{ $t('inventory_dashboard') }}</a></li>
              <li><a class="dropdown-item" href="#" @click.prevent="loadPreset('financial')"><AppIcon name="bi-cash-stack" class="bi bi-cash-stack me-2 text-warning" />{{ $t('financial_dashboard') }}</a></li>
              <li><hr class="dropdown-divider"></li>
              <li><a class="dropdown-item" href="#" @click.prevent="clearDashboard"><AppIcon name="bi-trash" class="bi bi-trash me-2 text-danger" />{{ $t('clear_dashboard') }}</a></li>
            </ul>
          </div> -->
          
          <template v-if="isEditing">
            <button
              class="btn btn-outline-danger btn-sm shadow-sm"
              @click="clearDashboard"
              :disabled="activeTabWidgets.length === 0"
              :title="$t('delete_all_insights') || $t('clear_dashboard')"
            >
              <AppIcon name="bi-trash3" class="bi bi-trash3 me-1" />
              {{ $t('delete_all_insights') || $t('clear_dashboard') }}
            </button>
            <!-- Pre-change state (Subtle/Outline buttons) -->
            <template v-if="!hasUnsavedChanges">
              <button class="btn btn-outline-secondary btn-sm shadow-sm ms-2" @click="cancelEdit" :title="$t('cancel_edit')">
                <AppIcon name="bi-x-lg" class="bi bi-x-lg me-1" />
                {{ $t('cancel') }}
              </button>
              <button class="btn btn-outline-success btn-sm shadow-sm ms-2" @click="saveLayout" :title="$t('save_template')">
                <AppIcon name="bi-check-lg" class="bi bi-check-lg me-1" />
                {{ $t('save') }}
              </button>
            </template>
            <!-- Changed state (Highly highlighted Discard and Save Changes buttons with pulse) -->
            <template v-else>
              <button class="btn btn-danger btn-sm px-3 shadow-lg fw-bold border-2" @click="cancelEdit" :title="$t('cancel_edit')">
                <AppIcon name="bi-arrow-counterclockwise" class="bi bi-arrow-counterclockwise me-1" />
                {{ $t('discard') || 'Discard' }}
              </button>
              <button class="btn btn-success btn-sm px-4 shadow-lg fw-bold border-2 animation-pulse-green" @click="saveLayout" :title="$t('save_template')">
                <AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill me-1" />
                {{ $t('save_changes') || 'Save Changes' }}
              </button>
            </template>
          </template>
        </div>
      </div>
    </div>

    <!-- Dashboard Tabs & Filter Bar -->
    <div class="dashboard-tabs-bar d-flex justify-content-between align-items-end flex-wrap gap-3">
      <div class="tabs-scroll-container flex-grow-1">
        <div class="dashboard-tabs">
          <div
            v-for="tab in tabs"
            :key="tab.id"
            class="dashboard-tab"
            :class="{ active: activeTabId === tab.id }"
            @click="switchTab(tab.id)"
          >
            <AppIcon :name="'bi ' + (tab.icon || 'bi-grid-1x2')" :class="'bi ' + (tab.icon || 'bi-grid-1x2')" class="tab-icon" />
            <span class="tab-label">{{ tab.nameKey ? $t(tab.nameKey) : tab.name }}</span>
            <div class="tab-actions" v-if="tabs.length > 1" @click.stop>
              <div class="dropdown">
                <button class="btn btn-link btn-sm p-0 tab-menu-btn" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport" data-boundary="viewport" aria-expanded="false">
                  <AppIcon name="bi-three-dots" class="bi bi-three-dots" />
                </button>
                <ul class="dropdown-menu dropdown-menu-end shadow-sm">
                  <li><a class="dropdown-item" href="#" @click.prevent.stop="showRenameTabModal(tab)"><AppIcon name="bi-pencil" class="bi bi-pencil me-2" />{{ $t('rename') }}</a></li>
                  <li><hr class="dropdown-divider"></li>
                  <li><a class="dropdown-item text-danger" href="#" @click.prevent.stop="deleteTab(tab.id)"><AppIcon name="bi-trash" class="bi bi-trash me-2" />{{ $t('delete') }}</a></li>
                </ul>
              </div>
            </div>
          </div>
          <button class="add-tab-btn" @click="addNewTab" :title="$t('add_dashboard')">
            <AppIcon name="bi-plus-lg" class="bi bi-plus-lg" />
          </button>
        </div>
      </div>

      <!-- Inline Date Filters (No Refresh Button) -->
      <div class="d-flex align-items-center gap-2 mb-2">
        <div class="filter-group">
          <select v-model="dateRangeOption" @change="handleDateRangeChange" class="form-select form-select-sm" style="min-width: 140px;">
            <option value="">{{ $t('filter_by_date') }}</option>
            <option value="today">{{ $t('today') }}</option>
            <option value="yesterday">{{ $t('yesterday') }}</option>
            <option value="this_week">{{ $t('this_week') }}</option>
            <option value="last_week">{{ $t('last_week') }}</option>
            <option value="this_month">{{ $t('this_month') }}</option>
            <option value="last_month">{{ $t('last_month') }}</option>
            <option value="this_quarter">{{ $t('this_quarter') }}</option>
            <option value="this_year">{{ $t('this_year') }}</option>
            <option value="last_year">{{ $t('last_year') }}</option>
            <option value="custom">{{ $t('custom_date') }}</option>
          </select>
        </div>
        <div v-if="dateRangeOption === 'custom'" style="min-width: 200px;">
          <date-picker :clearable="true" :editable="false" class="w-100" v-model="range" type="date" range :multi-calendars="1" :placeholder="$t('select_date_range')"></date-picker>
        </div>
      </div>
    </div>

    <!-- Premium Empty State -->
    <div v-if="!isLoadingLayout && activeTabWidgets.length === 0" class="empty-state-container">
      <div class="empty-state-card">
        <div class="empty-state-icon">
          <AppIcon name="bi-bar-chart-line" class="bi bi-bar-chart-line" />
        </div>
        <h4 class="mt-4 fw-bold">{{ $t('no_widgets_added') }}</h4>
        <p class="text-muted mb-4">{{ $t('start_by_adding_widgets') }}</p>
        
        <div class="d-flex flex-wrap gap-2 justify-content-center mb-4">
          <button class="btn btn-primary" @click="showAddWidgetModal">
            <AppIcon name="bi-plus-lg" class="bi bi-plus-lg me-2" />{{ $t('add_first_widget') }}
          </button>
          <button class="btn btn-outline-secondary" @click="loadPreset('sales')">
            <AppIcon name="bi-lightning" class="bi bi-lightning me-2" />{{ $t('load_sales_preset') }}
          </button>
        </div>
        
        <div class="preset-grid">
          <div class="preset-item" :class="{ disabled: !isPresetAllowedForPlan('sales') }" @click="isPresetAllowedForPlan('sales') && loadPreset('sales')">
            <AppIcon name="bi-receipt" class="bi bi-receipt text-success" />
            <span>{{ $t('sales') }}</span>
          </div>
          <div class="preset-item" :class="{ disabled: !isPresetAllowedForPlan('inventory') }" @click="isPresetAllowedForPlan('inventory') && loadPreset('inventory')">
            <AppIcon name="bi-box-seam" class="bi bi-box-seam text-primary" />
            <span>{{ $t('inventory') }}</span>
          </div>
          <div class="preset-item" :class="{ disabled: !isPresetAllowedForPlan('purchases') }" @click="isPresetAllowedForPlan('purchases') && loadPreset('purchases')">
            <AppIcon name="bi-truck" class="bi bi-truck text-warning" />
            <span>{{ $t('purchases') }}</span>
          </div>
          <div class="preset-item" :class="{ disabled: !isPresetAllowedForPlan('profits') }" @click="isPresetAllowedForPlan('profits') && loadPreset('profits')">
            <AppIcon name="bi-cash-coin" class="bi bi-cash-coin text-success" />
            <span>{{ $t('profits') }}</span>
          </div>
          <div class="preset-item" :class="{ disabled: !isPresetAllowedForPlan('financial') }" @click="isPresetAllowedForPlan('financial') && loadPreset('financial')">
            <AppIcon name="bi-wallet2" class="bi bi-wallet2 text-primary" />
            <span>{{ $t('finance') }}</span>
          </div>
          <div class="preset-item" :class="{ disabled: !isPresetAllowedForPlan('pos') }" @click="isPresetAllowedForPlan('pos') && loadPreset('pos')">
            <AppIcon name="bi-shop" class="bi bi-shop text-info" />
            <span>{{ $t('pos') }}</span>
          </div>
          <!-- <div class="preset-item" @click="loadPreset('hr')">
            <AppIcon name="bi-people" class="bi bi-people text-info" />
            <span>{{ $t('hr') }}</span>
          </div> -->
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoadingLayout" class="text-center py-5 my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Draggable Grid -->
    <draggable
      v-model="activeTabWidgets"
      class="row g-3"
      draggable=".draggable-item"
      @end="onDragEnd"
      ghost-class="ghost-card"
      :disabled="!isEditing"
    >
      <div
        v-for="(widget, index) in activeTabWidgets"
        :key="widget.id"
        class="draggable-item"
        :class="widgetWidthClass(widget)"
      >
        <div class="card h-100 shadow-sm border-0 widget-card" :class="`widget-${widget.module || 'default'}`" :style="widget.color ? `border-left: 4px solid ${widget.color} !important` : ''">
          <div class="card-header d-flex justify-content-between align-items-center bg-transparent border-0 pt-3 px-3">
            <div class="d-flex align-items-center text-muted">
                <div class="widget-icon me-2" :style="widget.color ? `background: ${widget.color}15; color: ${widget.color}` : ''">
                  <AppIcon :name="`bi ${widget.icon}`" :class="`bi ${widget.icon}`" />
                </div>
                <div class="d-flex flex-column text-start">
                    <div v-if="widget.type !== 'text'" class="d-flex align-items-center gap-1">
                      <h6 class="mb-0 text-uppercase small fw-bold">{{ widget.customTitle || $t(widget.title) }}</h6>
                      <button
                        type="button"
                        class="insight-help-button"
                        :aria-label="`${$t('description')}: ${$t(widget.description || 'widget_description')}`"
                        @mousedown.stop
                        @click.stop
                      >
                        <AppIcon name="bi-question-circle" class="bi bi-question-circle" />
                        <span class="insight-help-tooltip" role="tooltip">
                          {{ $t(widget.description || 'widget_description') }}
                        </span>
                      </button>
                    </div>
                    <small v-if="showWidgetDateRanges && !['text', 'payment_sales_summary'].includes(widget.type)" class="text-muted" style="font-size: 0.65rem; margin-top: 2px;">
                        <AppIcon name="bi-calendar3" class="bi bi-calendar3 me-1" />
                        {{ widgetDateLabel(widget) }}
                    </small>
                </div>
            </div>
            <div class="dropdown" @mousedown.stop>
                <button
                  class="btn btn-sm btn-link text-muted p-0 widget-menu-btn"
                  type="button"
                  data-bs-toggle="dropdown"
                  data-bs-boundary="viewport"
                  data-boundary="viewport"
                  aria-expanded="false"
                  @mousedown.stop
                >
                    <AppIcon name="bi-three-dots-vertical" class="bi bi-three-dots-vertical" />
                </button>
                <ul class="dropdown-menu dropdown-menu-end shadow-sm" style="max-height: 300px; overflow-y: auto;" @mousedown.stop>
                    <li v-if="!isDateAxisChart(widget)"><a class="dropdown-item" href="#" @click.prevent="resizeWidget(index, 'col-md-3 col-lg-3')"><AppIcon name="bi-square" class="bi bi-square me-2" />{{ $t('small') }} (25%)</a></li>
                    <li v-if="!isDateAxisChart(widget)"><a class="dropdown-item" href="#" @click.prevent="resizeWidget(index, 'col-md-6 col-lg-6')"><AppIcon name="bi-layout-split" class="bi bi-layout-split me-2" />{{ $t('medium') }} (50%)</a></li>
                    <li><a class="dropdown-item" href="#" @click.prevent="resizeWidget(index, 'col-12')"><AppIcon name="bi-layout-sidebar-inset" class="bi bi-layout-sidebar-inset me-2" />{{ $t('large') }} (100%)</a></li>
                    <li><hr class="dropdown-divider"></li>
                    <li><a class="dropdown-item text-danger" href="#" @click.prevent="removeWidget(index)"><AppIcon name="bi-trash" class="bi bi-trash me-2" />{{ $t('remove') }}</a></li>
                </ul>
            </div>
          </div>
          <div class="card-body d-flex flex-column align-items-center justify-content-center pt-2 pb-4">
            <div v-if="widget.loading" class="widget-loading">
              <div class="spinner-border text-primary spinner-border-sm" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
            <div v-else class="text-center w-100">
               <!-- Number Widget -->
               <div v-if="widget.type === 'number'" class="number-widget">
                   <h2 :class="[widget.width && widget.width.includes('col-lg-3') ? 'display-6' : 'display-4', 'fw-bold mb-1']" :style="widget.color ? `color: ${widget.color}` : ''">
                     {{ formatValue(widget) }}
                   </h2>
                   <div v-if="showWidgetComparisons && widget.trend !== undefined" class="trend-indicator">
                     <span :class="(widget.trend >= 0 ? 'text-success' : 'text-danger') + ' fw-semibold'">
                       <AppIcon :name="widget.trend >= 0 ? 'bi bi-arrow-up' : 'bi bi-arrow-down'" :class="widget.trend >= 0 ? 'bi bi-arrow-up' : 'bi bi-arrow-down'" />
                       {{ Math.abs(widget.trend) }}%
                     </span>
                   </div>
               </div>

               <!-- Current Payment Sales Widget -->
               <div v-else-if="widget.type === 'payment_sales_summary'" class="payment-sales-summary-widget w-100">
                 <div class="payment-sales-summary-grid">
                   <div class="payment-sales-summary-period payment-sales-summary-period--today">
                     <span class="payment-sales-summary-icon" aria-hidden="true">
                       <AppIcon name="bi-calendar-check" class="bi bi-calendar-check" />
                     </span>
                     <div>
                       <small>{{ $t('total_sales_today') }}</small>
                       <strong>{{ formatValue({ value: widget.value && widget.value.today, format: 'currency' }) }}</strong>
                     </div>
                   </div>
                   <div class="payment-sales-summary-period payment-sales-summary-period--week">
                     <span class="payment-sales-summary-icon" aria-hidden="true">
                       <AppIcon name="bi-calendar-week" class="bi bi-calendar-week" />
                     </span>
                     <div>
                       <small>{{ $t('total_sales_week') }}</small>
                       <strong>{{ formatValue({ value: widget.value && widget.value.week, format: 'currency' }) }}</strong>
                     </div>
                   </div>
                   <div class="payment-sales-summary-period payment-sales-summary-period--month">
                     <span class="payment-sales-summary-icon" aria-hidden="true">
                       <AppIcon name="bi-calendar3" class="bi bi-calendar3" />
                     </span>
                     <div>
                       <small>{{ $t('total_sales_month') }}</small>
                       <strong>{{ formatValue({ value: widget.value && widget.value.month, format: 'currency' }) }}</strong>
                     </div>
                   </div>
                 </div>
                 <div class="payment-sales-summary-scope">
                   <AppIcon name="bi-check-circle" class="bi bi-check-circle me-1" aria-hidden="true" />
                   {{ $t('paid_transactions_only') }}
                 </div>
               </div>

               <!-- Sales Revenue Breakdown Widget -->
               <div v-else-if="widget.type === 'sales_revenue_breakdown'" class="sales-revenue-breakdown w-100">
                 <div class="sales-revenue-total">
                   <small class="text-muted d-block text-uppercase">{{ $t('total_sales') }}</small>
                   <h3 class="fw-bold mb-0 text-primary">
                     {{ formatValue({ value: widget.value && widget.value.total_sales, format: 'currency' }) }}
                   </h3>
                 </div>
                 <div class="row g-2 mt-2">
                   <div class="col-4">
                     <div class="sales-revenue-status sales-revenue-status-paid">
                       <small>{{ $t('paid') }}</small>
                       <strong>{{ formatValue({ value: widget.value && widget.value.paid, format: 'currency' }) }}</strong>
                     </div>
                   </div>
                   <div class="col-4">
                     <div class="sales-revenue-status sales-revenue-status-pending">
                       <small>{{ $t('pending') }}</small>
                       <strong>{{ formatValue({ value: widget.value && widget.value.pending, format: 'currency' }) }}</strong>
                     </div>
                   </div>
                   <div class="col-4">
                     <div class="sales-revenue-status sales-revenue-status-canceled">
                       <small>{{ $t('canceled') }}</small>
                       <strong>{{ formatValue({ value: widget.value && widget.value.canceled, format: 'currency' }) }}</strong>
                     </div>
                   </div>
                 </div>
               </div>
               
               <!-- Financial Category Breakdown -->
               <div
                 v-else-if="isFinancialCategoryBreakdown(widget)"
                 class="financial-category-breakdown"
                 :class="widget.chartType === 'financial_income_by_category' ? 'is-income' : 'is-expense'"
               >
                 <div v-if="widget.categoryBreakdown && widget.categoryBreakdown.length" class="financial-category-list">
                   <div
                     v-for="(item, itemIndex) in widget.categoryBreakdown"
                     :key="item.category_id || item.source_type || `uncategorized-${itemIndex}`"
                     class="financial-category-row"
                   >
                     <div class="financial-category-content">
                       <span class="financial-category-rank">{{ itemIndex + 1 }}</span>
                       <span class="financial-category-name">{{ getFinancialCategoryName(item, widget) }}</span>
                       <strong class="financial-category-amount">
                         ({{ formatValue({ value: item.total_amount, format: 'currency' }) }})
                       </strong>
                     </div>
                     <div class="financial-category-track" aria-hidden="true">
                       <span :style="{ width: `${item.percentage}%` }"></span>
                     </div>
                   </div>
                 </div>
                 <div v-else class="financial-category-empty">
                   <AppIcon name="bi-inbox" class="bi bi-inbox" />
                   <span>{{ $t('no_data') }}</span>
                 </div>
               </div>

               <!-- Bar Chart Card Breakdown -->
               <div
                 v-else-if="isBarChart(widget)"
                 class="financial-category-breakdown bar-chart-breakdown"
               >
                 <div v-if="widget.barCardItems && widget.barCardItems.length" class="financial-category-list">
                   <div
                     v-for="(item, itemIndex) in widget.barCardItems"
                     :key="`${item.label}-${itemIndex}`"
                     class="financial-category-row"
                   >
                     <div class="financial-category-content">
                       <span
                         class="financial-category-rank"
                         :style="{ color: item.color }"
                       >
                         {{ itemIndex + 1 }}
                       </span>
                       <span class="financial-category-name">{{ item.label }}</span>
                       <strong class="financial-category-amount" :style="{ color: item.color }">
                         ({{ formatBarCardValue(widget, item.value) }})
                       </strong>
                     </div>
                     <div class="financial-category-track" aria-hidden="true">
                       <span :style="{ width: `${item.percentage}%`, backgroundColor: item.color }"></span>
                     </div>
                   </div>
                 </div>
                 <div v-else class="financial-category-empty">
                   <AppIcon name="bi-inbox" class="bi bi-inbox" />
                   <span>{{ $t('no_data') }}</span>
                 </div>
               </div>

               <!-- Chart Widget -->
               <div v-else-if="widget.type === 'chart'" class="chart-widget" style="width: 100%; min-height: 180px;">
                 <chart-js-widget :type="widget.chartJsType || 'bar'" :data="widget.chartData" :options="widget.chartOptions"></chart-js-widget>
               </div>
               
               <!-- Text Widget -->
               <div v-else-if="widget.type === 'text'" class="text-start text-widget">
                 <h4 class="fw-bold mb-3">{{ widget.textTitle || widget.title }}</h4>
                 <p class="text-muted mb-0" style="white-space: pre-wrap;">{{ widget.textContent || '' }}</p>
               </div>
               
               <!-- Table Widget -->
               <div v-else-if="widget.type === 'table'" class="table-widget w-100">
                 <div class="table-responsive" style="max-height: 250px;">
                   <table class="table table-sm table-hover mb-0">
                     <thead class="bg-light">
                       <tr>
                         <th v-for="col in widget.columns" :key="col.key" class="small text-muted">{{ $t(col.label) }}</th>
                       </tr>
                     </thead>
                     <tbody>
                       <tr v-for="(row, rowIndex) in widget.tableData" :key="rowIndex">
                         <td v-for="col in widget.columns" :key="col.key" class="small">
                            {{ col.format ? formatValue({format: col.format, value: row[col.key]}) : row[col.key] }}
                         </td>
                       </tr>
                       <tr v-if="!widget.tableData || widget.tableData.length === 0">
                         <td :colspan="widget.columns ? widget.columns.length : 1" class="text-center text-muted small py-3">
                           {{ $t('no_data') }}
                         </td>
                       </tr>
                     </tbody>
                   </table>
                 </div>
               </div>
               
               <!-- Product Search Profitability Widget -->
               <div v-else-if="widget.type === 'product_search_profitability'" class="product-search-widget w-100">
                 <div class="mb-3">
                   <auto-complete 
                     entity="inventories" 
                     emitEvent="searchProductEvent" 
                     :uid="index"
                     @searchProductEvent="handleProductSearchEvent"
                     :defaultValue="widget.productName"
                   ></auto-complete>
                 </div>
                 <div v-if="widget.dataLoading" class="text-center py-3">
                    <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                 </div>
                 <div v-else-if="widget.searchResult" class="row text-center mt-3 g-2">
                    <div class="col-4">
                        <small class="text-muted d-block mb-1">{{ $t('sales') }}</small>
                        <h6 class="fw-bold text-success" style="font-size: 0.9rem;">{{ formatValue({value: widget.searchResult.total_sales, format: 'currency'}) }}</h6>
                    </div>
                    <div class="col-4">
                        <small class="text-muted d-block mb-1">{{ $t('purchases') }}</small>
                        <h6 class="fw-bold text-danger" style="font-size: 0.9rem;">{{ formatValue({value: widget.searchResult.total_purchases, format: 'currency'}) }}</h6>
                    </div>
                    <div class="col-4">
                        <small class="text-muted d-block mb-1">{{ $t('profit') }}</small>
                        <h6 class="fw-bold text-primary" style="font-size: 0.9rem;">{{ formatValue({value: widget.searchResult.profit, format: 'currency'}) }}</h6>
                    </div>
                 </div>
                 <div v-else class="text-center py-4 text-muted small">
                    <AppIcon name="bi-search" class="bi bi-search d-block mb-2" style="font-size: 20px;" />
                    {{ $t('search_product_to_view') || 'Search a product to view' }}
                 </div>
               </div>
               
               <!-- Quotes Stats Widget -->
               <div v-else-if="widget.type === 'quotes_stats'" class="quotes-stats-widget w-100">
                  <div v-if="widget.value" class="row text-center mt-2 g-2">
                     <div class="col-6 border-end">
                         <small class="text-muted d-block mb-1 text-uppercase" style="font-size: 0.65rem;">{{ $t('total') }}</small>
                         <h4 class="fw-bold text-primary mb-0">{{ widget.value.total }}</h4>
                     </div>
                     <div class="col-6">
                         <small class="text-muted d-block mb-1 text-uppercase" style="font-size: 0.65rem;">{{ $t('confirmed') }}</small>
                         <h4 class="fw-bold text-success mb-0">{{ widget.value.confirmed }}</h4>
                     </div>
                  </div>
               </div>
                
               <!-- Invoices Stats Widget -->
               <div v-else-if="widget.type === 'invoices_stats'" class="invoices-stats-widget w-100">
                  <div v-if="widget.value" class="row text-center mt-2 g-2">
                     <div class="col-6 border-end">
                         <small class="text-muted d-block mb-1 text-uppercase" style="font-size: 0.65rem;">{{ $t('total') }}</small>
                         <h4 class="fw-bold text-primary mb-0">{{ widget.value.total }}</h4>
                     </div>
                     <div class="col-6">
                         <small class="text-muted d-block mb-1 text-uppercase" style="font-size: 0.65rem;">{{ $t('paid') }}</small>
                         <h4 class="fw-bold text-success mb-0">{{ widget.value.paid }}</h4>
                     </div>
                  </div>
               </div>
                
               <!-- Gauge/Progress Widget -->
               <div v-else-if="widget.type === 'gauge'" class="gauge-widget">
                 <div class="gauge-container">
                   <svg viewBox="0 0 100 50" class="gauge-svg">
                     <path d="M 10 50 A 40 40 0 0 1 90 50" fill="none" stroke="#e9ecef" stroke-width="8" stroke-linecap="round"/>
                     <path d="M 10 50 A 40 40 0 0 1 90 50" fill="none" :stroke="widget.color || '#0d6efd'" stroke-width="8" stroke-linecap="round" :stroke-dasharray="`${(widget.progress || 0) * 1.26} 126`"/>
                   </svg>
                   <div class="gauge-value">
                     <h3 class="mb-0 fw-bold" :style="widget.color ? `color: ${widget.color}` : ''">{{ widget.value }}{{ widget.suffix || '%' }}</h3>
                   </div>
                 </div>
                 <small class="text-muted">{{ $t('goal') }}: {{ widget.target || 100 }}{{ widget.suffix || '%' }}</small>
               </div>
               
               <!-- Comparison Widget -->
               <div v-else-if="showWidgetComparisons && widget.type === 'comparison'" class="comparison-widget">
                 <div class="d-flex align-items-center justify-content-center gap-4">
                   <div class="text-center">
                     <h3 class="mb-0 fw-bold" :style="widget.color ? `color: ${widget.color}` : ''">{{ formatValue({...widget, value: widget.currentValue}) }}</h3>
                     <small class="text-muted">{{ $t('current') }}</small>
                   </div>
                   <div class="comparison-arrow">
                     <AppIcon name="bi-arrow-up-circle-fill" :class="widget.trend >= 0 ? 'bi bi-arrow-up-circle-fill text-success' : 'bi bi-arrow-down-circle-fill text-danger'" style="font-size: 1.5rem;" />
                   </div>
                   <div class="text-center">
                     <h3 class="mb-0 fw-bold text-muted">{{ formatValue({...widget, value: widget.previousValue}) }}</h3>
                     <small class="text-muted">{{ $t('previous') }}</small>
                   </div>
                 </div>
                 <div class="mt-2">
                   <span :class="widget.trend >= 0 ? 'badge bg-success-subtle text-success' : 'badge bg-danger-subtle text-danger'">
                     {{ widget.trend >= 0 ? '+' : '' }}{{ widget.trend }}%
                   </span>
                 </div>
               </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="hasAvailableWidgetsToAdd"
        key="add-widget-tile"
        class="col-md-4 col-lg-3"
      >
        <button
          type="button"
          class="add-widget-tile"
          @click="showAddWidgetModal"
          :title="$t('add_widget')"
        >
          <span class="add-widget-tile-icon">
            <AppIcon name="bi-plus-lg" class="bi bi-plus-lg" />
          </span>
          <span class="add-widget-tile-label">{{ $t('add_widget') }}</span>
        </button>
      </div>
    </draggable>

    <!-- Add Widget Modal -->
    <div class="modal fade" id="addWidgetModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-xl add-widget-modal-dialog">
        <div class="modal-content border-0 shadow">
          <div class="modal-header">
            <h5 class="modal-title fw-bold">{{ $t('add_widget') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-0">
            <div class="widget-catalog">
              <!-- Module Sidebar -->
              <aside class="widget-module-sidebar">
                <div class="widget-module-sidebar-title">{{ $t('modules') }}</div>
                <nav class="widget-module-nav" :aria-label="$t('modules')">
                  <button
                    v-for="module in availableWidgetModules"
                    :key="module.key"
                    type="button"
                    class="widget-module-button"
                    :class="{ active: selectedWidgetModule === module.key }"
                    :aria-pressed="selectedWidgetModule === module.key ? 'true' : 'false'"
                    @click="selectedWidgetModule = module.key"
                  >
                    <span class="widget-module-icon">
                      <AppIcon :name="`bi ${module.icon}`" :class="`bi ${module.icon}`" />
                    </span>
                    <span class="widget-module-name">{{ $t(module.label) }}</span>
                    <span class="widget-module-count">{{ module.count }}</span>
                  </button>
                </nav>
              </aside>

              <section class="widget-catalog-content">
                <!-- Search Input -->
                <div class="widget-catalog-toolbar">
                  <div class="widget-search">
                    <AppIcon name="bi-search" class="bi bi-search" />
                    <input
                      type="text"
                      v-model="widgetSearchQuery"
                      class="form-control"
                      :placeholder="$t('search') + '...'"
                    >
                    <button
                      v-if="widgetSearchQuery"
                      type="button"
                      class="widget-search-clear"
                      :aria-label="$t('cancel')"
                      @click="widgetSearchQuery = ''"
                    >
                      <AppIcon name="bi-x-lg" class="bi bi-x-lg" />
                    </button>
                  </div>
                </div>

                <!-- Widget List -->
                <div class="widget-catalog-list list-group list-group-flush">
                  <section v-if="recommendedWidgets.length" class="beginner-recommendations">
                    <div class="beginner-recommendations-heading">
                      <div class="beginner-recommendations-icon" aria-hidden="true">
                        <AppIcon name="bi-lightbulb" class="bi bi-lightbulb" />
                      </div>
                      <div>
                        <h6 class="mb-1">{{ $t('recommended_insights') }}</h6>
                        <p class="mb-0">{{ $t(beginnerRecommendationDescriptionKey) }}</p>
                      </div>
                    </div>
                    <div class="beginner-recommendations-grid">
                      <button
                        v-for="option in recommendedWidgets"
                        :key="`recommended-${option.module}-${option.title}`"
                        type="button"
                        class="beginner-recommendation-card"
                        @click="addWidget(option)"
                      >
                        <span class="widget-option-icon">
                          <AppIcon :name="`bi ${option.icon}`" :class="`bi ${option.icon}`" />
                        </span>
                        <span class="widget-option-copy flex-grow-1">
                          <strong>{{ $t(option.title) }}</strong>
                          <small>{{ $t(option.description || 'widget_description') }}</small>
                        </span>
                        <span class="widget-option-add">
                          <AppIcon name="bi-plus-lg" class="bi bi-plus-lg" />
                        </span>
                      </button>
                    </div>
                  </section>

                  <div v-for="(group, moduleName) in displayedWidgetGroups" :key="`group-${moduleName}`">
                    <div
                      v-if="selectedWidgetModule === 'all'"
                      class="widget-module-heading list-group-item"
                    >
                      {{ $t(moduleName) }}
                      <span>{{ group.length }}</span>
                    </div>
                    <button
                      v-for="option in group"
                      :key="`${moduleName}-${option.title}`"
                      type="button"
                      class="widget-option list-group-item list-group-item-action d-flex align-items-center"
                      :class="{ 'locked-widget-option': !isWidgetAllowedForPlan(option) }"
                      :disabled="!isWidgetAllowedForPlan(option)"
                      @click="addWidget(option)"
                    >
                      <div class="widget-option-icon">
                        <AppIcon :name="`bi ${option.icon}`" :class="`bi ${option.icon}`" />
                      </div>
                      <div class="widget-option-copy flex-grow-1">
                        <h6 class="mb-1">{{ $t(option.title) }}</h6>
                        <small class="text-muted">{{ $t(option.description || 'widget_description') }}</small>
                      </div>
                      <span v-if="!isWidgetAllowedForPlan(option)" class="locked-widget-badge ms-3">
                        <AppIcon name="bi-lock-fill" class="bi bi-lock-fill me-1" />
                        Pro
                      </span>
                      <span v-else class="widget-option-add ms-3">
                        <AppIcon name="bi-plus-lg" class="bi bi-plus-lg" />
                      </span>
                    </button>
                  </div>

                  <div v-if="recommendedWidgets.length === 0 && Object.keys(displayedWidgetGroups).length === 0" class="widget-catalog-empty text-center text-muted">
                    <AppIcon name="bi-search" class="bi bi-search" />
                    <p class="mb-0">{{ $t('no_results_found') }}</p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Text Widget Configuration Modal -->
    <div class="modal fade" id="textWidgetModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0 shadow">
          <div class="modal-header border-bottom-0 pb-2">
            <h5 class="modal-title fw-bold">{{ $t('configure_text_widget') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label fw-bold">{{ $t('title') }}</label>
              <input 
                type="text" 
                v-model="textWidgetConfig.title" 
                class="form-control" 
                :placeholder="$t('enter_title')"
              >
            </div>
            <div class="mb-3">
              <label class="form-label fw-bold">{{ $t('description') }}</label>
              <textarea 
                v-model="textWidgetConfig.content" 
                class="form-control" 
                rows="4"
                :placeholder="$t('enter_description')"
              ></textarea>
            </div>
          </div>
          <div class="modal-footer border-top-0">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('cancel') }}</button>
            <button type="button" class="btn btn-primary" @click="addTextWidget">{{ $t('add_widget') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Rename Tab Modal -->
    <div class="modal fade" id="renameTabModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-sm">
        <div class="modal-content border-0 shadow">
          <div class="modal-header border-bottom-0 pb-2">
            <h5 class="modal-title fw-bold">{{ $t('rename_dashboard') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label fw-bold">{{ $t('name') }}</label>
              <input
                type="text"
                v-model="renameTabConfig.name"
                class="form-control"
                :placeholder="$t('enter_name')"
                @keyup.enter="confirmRenameTab"
              >
            </div>
            <div class="mb-3">
              <label class="form-label fw-bold">{{ $t('icon') }}</label>
              <div class="tab-icon-picker">
                <button
                  v-for="icon in tabIconOptions"
                  :key="icon"
                  class="icon-option"
                  :class="{ selected: renameTabConfig.icon === icon }"
                  @click="renameTabConfig.icon = icon"
                  type="button"
                >
                  <AppIcon :name="'bi ' + icon" :class="'bi ' + icon" />
                </button>
              </div>
            </div>
          </div>
          <div class="modal-footer border-top-0">
            <button type="button" class="btn btn-secondary btn-sm" data-bs-dismiss="modal">{{ $t('cancel') }}</button>
            <button type="button" class="btn btn-primary btn-sm" @click="confirmRenameTab">{{ $t('save') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Tab Modal -->
    <div class="modal fade" id="addTabModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-sm">
        <div class="modal-content border-0 shadow">
          <div class="modal-header border-bottom-0 pb-2">
            <h5 class="modal-title fw-bold">{{ $t('add_dashboard') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label fw-bold">{{ $t('name') }}</label>
              <input
                type="text"
                v-model="newTabConfig.name"
                class="form-control"
                :placeholder="$t('dashboard_name_placeholder')"
                @keyup.enter="confirmAddTab"
              >
            </div>
            <div class="mb-3">
              <label class="form-label fw-bold">{{ $t('icon') }}</label>
              <div class="tab-icon-picker">
                <button
                  v-for="icon in tabIconOptions"
                  :key="icon"
                  class="icon-option"
                  :class="{ selected: newTabConfig.icon === icon }"
                  @click="newTabConfig.icon = icon"
                  type="button"
                >
                  <AppIcon :name="'bi ' + icon" :class="'bi ' + icon" />
                </button>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label fw-bold">{{ $t('template') }}</label>
              <select v-model="newTabConfig.preset" class="form-select form-select-sm">
                <option value="">{{ $t('empty_dashboard') }}</option>
                <option value="sales">{{ $t('sales_dashboard') }}</option>
                <option value="inventory">{{ $t('inventory_dashboard') }}</option>
                <option value="purchases">{{ $t('purchase_dashboard') }}</option>
                <option value="profits">{{ $t('profit_dashboard') }}</option>
                <option value="pos">{{ $t('pos_dashboard') }}</option>
                <option value="financial">{{ $t('financial_dashboard') }}</option>
              </select>
            </div>
          </div>
          <div class="modal-footer border-top-0">
            <button type="button" class="btn btn-secondary btn-sm" data-bs-dismiss="modal">{{ $t('cancel') }}</button>
            <button type="button" class="btn btn-primary btn-sm" @click="confirmAddTab">{{ $t('add') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Floating Save Button -->
    <transition name="fade-up">
      <div v-if="isEditing" class="floating-save-bar">
        <div class="floating-save-content">
          <span class="floating-save-hint">
            <AppIcon name="bi-pencil-square" class="bi bi-pencil-square me-1" />{{ $t('editing_mode', 'Editing Mode') }}
          </span>
          <div class="d-flex gap-2">
            <button type="button" class="btn btn-outline-danger btn-sm px-3" @click.stop="cancelEdit">
              <AppIcon name="bi-x-lg" class="bi bi-x-lg me-1" />{{ $t('cancel') }}
            </button>
            <button class="btn btn-success btn-sm px-4 fw-bold" @click="saveLayout">
              <AppIcon name="bi-check-lg" class="bi bi-check-lg me-1" />{{ $t('save') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import helper from '@/helper'
import ChartJsWidget from '@/components/ChartJsWidget.vue'
import AutoComplete from '@/components/autocomplete/AutoComplete.vue'
import moment from 'moment'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import { getBusinessPlanKey } from '@/constants/pricingPlans'

// Widget groups are presentation categories, so some of their names differ from
// the business module keys stored in Vuex.
const WIDGET_MODULE_KEYS = {
  inventory: ['inventory'],
  sales: ['sales'],
  purchases: ['purchases'],
  profit: ['sales'],
  pos: ['pos'],
  financial: ['finance'],
  leads: ['leads'],
  partners: ['crm'],
  logistics: ['sales'],
  invoice_status: ['sales']
}

const CURRENCY_CHART_TYPES = new Set([
  'revenue_trend',
  'advanced_sales_trend',
  'advanced_sales_by_client',
  'advanced_clients_by_revenue',
  'advanced_sales_by_payment_method',
  'advanced_purchases_by_supplier',
  'advanced_supplier_payment_status',
  'advanced_pos_sales_by_cashier',
  'advanced_pos_sales_by_register',
  'advanced_pos_daily_sales_trend',
  'advanced_pos_sales_by_product',
  'monthly_revenue',
  'top_customers',
  'pos_payment_methods',
  'pos_daily_sales',
  'inventory_stock_by_category',
  'inventory_stock_by_warehouse',
  'sales_by_category',
  'invoices_status_trend',
  'invoices_amount_distribution'
])

const FINANCIAL_CATEGORY_CHART_TYPES = new Set([
  'financial_income_by_category',
  'financial_expense_by_category'
])

const BAR_CHART_JS_TYPES = new Set(['bar', 'horizontalBar'])

const PERCENTAGE_CHART_TYPES = new Set([
  'advanced_profit_by_product',
  'advanced_profit_by_category'
])

// Time-series charts need the full horizontal axis to keep date labels legible.
// This also identifies legacy/saved widgets that predate an explicit metadata flag.
const DATE_AXIS_CHART_TYPES = new Set([
  'revenue_trend',
  'advanced_sales_trend',
  'monthly_revenue',
  'advanced_pos_daily_sales_trend',
  'pos_daily_sales',
  'invoices_status_trend'
])

const FULL_WIDTH_CLASS = 'col-12'

export default {
  name: 'AdvancedInsights',
  persistedFilterState: {
    key: 'advanced-insights',
    fields: ['dateRangeOption', 'range', 'start', 'end', 'selectedUserId', 'selectedWarehouseId'],
    dateFields: ['range'],
  },
  components: {
    draggable,
    ChartJsWidget,
    AutoComplete,
    DatePicker
  },
  data() {
    return {
      tabs: [
        { id: 'default', name: this.$t ? this.$t('dashboard') : 'Dashboard', icon: 'bi-grid-1x2', widgets: [] }
      ],
      activeTabId: 'default',
      isEditing: false,
      isLoadingLayout: true,
      // Temporarily hide per-widget date ranges and comparison values.
      showWidgetDateRanges: false,
      showWidgetComparisons: false,
      // Tab management
      renameTabConfig: { id: '', name: '', icon: '' },
      newTabConfig: { name: '', icon: 'bi-grid-1x2', preset: '' },
      renameTabModalInstance: null,
      addTabModalInstance: null,
      tabIconOptions: [
        'bi-grid-1x2', 'bi-receipt', 'bi-box-seam', 'bi-cash-stack',
        'bi-graph-up', 'bi-cart', 'bi-people', 'bi-truck',
        'bi-bar-chart', 'bi-pie-chart', 'bi-wallet2', 'bi-shop',
        'bi-calculator', 'bi-clipboard-data', 'bi-speedometer2', 'bi-kanban'
      ],
      hiddenInsightTitles: [
        'total_products',
        'out_of_stock',
        'total_suppliers',
        'total_purchases',
        'total_transactions',
        'total_clients',
        'total_orders',
        'total_quotes',
        'total_deliveries',
        'total_returns',
        'paid_invoices',
        'pending_invoices',
        'canceled_invoices',
        'overdue_invoices',
        'quotes_overview',
        'quotes_status_distribution',
        'invoices_amount_distribution',
        'average_ticket_size',
        'pos_refund_amount',
        'pos_sales_by_cashier',
        'pos_sales_by_shift',
        'pos_active_hours',
        'cash_register_difference',
        'pos_refunds_returns',
        'negative_stock_products',
        'negative_stock_product_list',
        'overstock_products',
        'overstock_product_list',
        'recent_stock_adjustments',
        'stock_movement_history',
        // Redundant summary cards when a more actionable list is available.
        'in_stock_products',
        'products_close_to_minimum_stock',
        'products_without_purchase_price',
        'products_without_sale_price',
        'expiring_products',
        'expired_products_in_stock',
        // Duplicate or weak variants of stronger catalog insights.
        'potential_selling_value',
        'fast_moving_products',
        'top_selling_products',
        'profit_margin_per_product',
        'best_seller_profit_comparison',
        'worst_selling_products',
        'revenue_trend',
        'monthly_revenue',
        'top_customers',
        'invoices_overview',
        'invoice_discount_sum',
        'invoice_total_sum',
        'invoice_vat_sum',
        'paid_invoiced_amount',
        'pending_invoiced_amount',
        'total_customers_balance',
        'total_invoices',
        'total_leads',
        'invoices_status_trend',
        // Fixed-period cards ignored the dashboard's selected date range.
        'total_sales_today',
        'total_sales_week',
        'total_sales_month'
      ],
      starterInsightTitles: [
        'total_sales',
        'payment_sales_summary',
        'income_sum',
        'expense_sum',
        'low_stock_products',
        'out_of_stock_products',
        'stock_value'
      ],
      beginnerRecommendations: {
        all: ['total_sales', 'income_sum', 'expense_sum', 'low_stock_products', 'stock_value'],
        inventory: ['low_stock_products', 'out_of_stock_products', 'stock_value', 'slow_moving_products'],
        sales: ['total_sales', 'sales_revenue_breakdown', 'sales_evolution', 'best_selling_products'],
        purchases: ['purchase_spend', 'supplier_payment_status', 'purchase_price_changes'],
        profit: ['gross_profit', 'profit_margin', 'low_margin_products'],
        pos: ['total_pos_sales', 'daily_sales_trend', 'sales_by_product'],
        financial: ['income_sum', 'expense_sum', 'net_income', 'income_by_category', 'expense_by_category'],
        leads: ['leads_by_status', 'leads_by_assigned_person'],
        partners: ['active_clients', 'repeat_customer_rate', 'inactive_clients'],
        logistics: ['total_deliveries', 'total_returns'],
        invoice_status: ['invoices_status_distribution']
      },
      availableWidgets: [
        // Inventory Insights
        { 
          title: 'total_products', 
          type: 'number', 
          endpoint: '/insights/total-products', 
          dataKey: 'count', 
          icon: 'bi-box-seam', 
          description: 'total_products_desc',
          module: 'inventory'
        },
        { 
          title: 'out_of_stock', 
          type: 'number', 
          endpoint: '/insights/out-of-stock', 
          dataKey: 'count', 
          icon: 'bi-exclamation-triangle', 
          description: 'out_of_stock_desc',
          module: 'inventory'
        },
        {
          title: 'low_stock_products',
          type: 'number',
          endpoint: '/v2/insights/inventory',
          dataPath: 'low_stock_products',
          icon: 'bi-exclamation-circle',
          description: 'low_stock_products_desc',
          module: 'inventory',
          color: '#ffc107',
          compare: false
        },
        {
          title: 'out_of_stock_products',
          type: 'number',
          endpoint: '/v2/insights/inventory',
          dataPath: 'out_of_stock_products',
          icon: 'bi-x-octagon',
          description: 'out_of_stock_products_desc',
          module: 'inventory',
          color: '#dc3545',
          compare: false
        },
        {
          title: 'in_stock_products',
          type: 'number',
          endpoint: '/v2/insights/inventory',
          dataPath: 'in_stock_products',
          icon: 'bi-check-circle',
          description: 'in_stock_products_desc',
          module: 'inventory',
          color: '#198754',
          compare: false
        },
        {
          title: 'stock_value',
          type: 'number',
          endpoint: '/v2/insights/inventory',
          dataPath: 'current_stock_value',
          icon: 'bi-cash-stack',
          description: 'stock_value_desc',
          format: 'currency',
          module: 'inventory',
          color: '#198754',
          compare: false
        },
        {
          title: 'potential_selling_value',
          type: 'number',
          endpoint: '/v2/insights/inventory',
          dataPath: 'potential_selling_value',
          icon: 'bi-currency-exchange',
          description: 'potential_selling_value_desc',
          format: 'currency',
          module: 'inventory',
          color: '#0d6efd',
          compare: false
        },
        {
          title: 'potential_profit_in_stock',
          type: 'number',
          endpoint: '/v2/insights/inventory',
          dataPath: 'potential_profit_in_stock',
          icon: 'bi-graph-up-arrow',
          description: 'potential_profit_in_stock_desc',
          format: 'currency',
          module: 'inventory',
          color: '#20c997',
          compare: false
        },
        {
          title: 'products_close_to_minimum_stock',
          type: 'number',
          endpoint: '/v2/insights/inventory',
          dataPath: 'products_close_to_minimum_stock',
          icon: 'bi-speedometer',
          description: 'products_close_to_minimum_stock_desc',
          module: 'inventory',
          color: '#fd7e14',
          compare: false
        },
        {
          title: 'products_without_purchase_price',
          type: 'number',
          endpoint: '/v2/insights/inventory',
          dataPath: 'products_without_purchase_price',
          icon: 'bi-tag',
          description: 'products_without_purchase_price_desc',
          module: 'inventory',
          color: '#6f42c1',
          compare: false
        },
        {
          title: 'products_without_sale_price',
          type: 'number',
          endpoint: '/v2/insights/inventory',
          dataPath: 'products_without_sale_price',
          icon: 'bi-tags',
          description: 'products_without_sale_price_desc',
          module: 'inventory',
          color: '#0dcaf0',
          compare: false
        },
        {
          title: 'expiring_products',
          type: 'number',
          endpoint: '/v2/insights/inventory',
          dataPath: 'expiring_products',
          icon: 'bi-calendar2-event',
          description: 'expiring_products_desc',
          module: 'inventory',
          color: '#ffc107',
          compare: false
        },
        {
          title: 'expired_products_in_stock',
          type: 'number',
          endpoint: '/v2/insights/inventory',
          dataPath: 'expired_products_in_stock',
          icon: 'bi-calendar2-x',
          description: 'expired_products_in_stock_desc',
          module: 'inventory',
          color: '#dc3545',
          compare: false
        },
        {
          title: 'low_stock_product_list',
          type: 'table',
          endpoint: '/v2/insights/inventory',
          dataPath: 'low_stock_product_list',
          icon: 'bi-list-check',
          description: 'low_stock_product_list_desc',
          module: 'inventory',
          columns: [
            { label: 'product', key: 'product_name' },
            { label: 'sku', key: 'sku' },
            { label: 'quantity', key: 'quantity' },
            { label: 'min_quantity', key: 'min_quantity' }
          ]
        },
        {
          title: 'products_close_to_minimum_stock_list',
          type: 'table',
          endpoint: '/v2/insights/inventory',
          dataPath: 'products_close_to_minimum_stock_list',
          icon: 'bi-speedometer',
          description: 'products_close_to_minimum_stock_list_desc',
          module: 'inventory',
          columns: [
            { label: 'product', key: 'product_name' },
            { label: 'sku', key: 'sku' },
            { label: 'quantity', key: 'quantity' },
            { label: 'min_quantity', key: 'min_quantity' }
          ]
        },
        {
          title: 'out_of_stock_product_list',
          type: 'table',
          endpoint: '/v2/insights/inventory',
          dataPath: 'out_of_stock_product_list',
          icon: 'bi-clipboard-x',
          description: 'out_of_stock_product_list_desc',
          module: 'inventory',
          columns: [
            { label: 'product', key: 'product_name' },
            { label: 'sku', key: 'sku' },
            { label: 'quantity', key: 'quantity' }
          ]
        },
        {
          title: 'products_without_purchase_price_list',
          type: 'table',
          endpoint: '/v2/insights/inventory',
          dataPath: 'products_without_purchase_price_list',
          icon: 'bi-tag',
          description: 'products_without_purchase_price_list_desc',
          module: 'inventory',
          columns: [
            { label: 'product', key: 'product_name' },
            { label: 'sku', key: 'sku' },
            { label: 'quantity', key: 'quantity' },
            { label: 'purchase_price', key: 'purchase_price', format: 'currency' }
          ]
        },
        {
          title: 'products_without_sale_price_list',
          type: 'table',
          endpoint: '/v2/insights/inventory',
          dataPath: 'products_without_sale_price_list',
          icon: 'bi-tags',
          description: 'products_without_sale_price_list_desc',
          module: 'inventory',
          columns: [
            { label: 'product', key: 'product_name' },
            { label: 'sku', key: 'sku' },
            { label: 'quantity', key: 'quantity' },
            { label: 'price', key: 'price', format: 'currency' }
          ]
        },
        {
          title: 'expiring_product_list',
          type: 'table',
          endpoint: '/v2/insights/inventory',
          dataPath: 'expiring_product_list',
          icon: 'bi-calendar2-event',
          description: 'expiring_product_list_desc',
          module: 'inventory',
          columns: [
            { label: 'product', key: 'product_name' },
            { label: 'sku', key: 'sku' },
            { label: 'quantity', key: 'quantity' },
            { label: 'expiration_date', key: 'expiration_date', format: 'date' }
          ]
        },
        {
          title: 'expired_products_in_stock_list',
          type: 'table',
          endpoint: '/v2/insights/inventory',
          dataPath: 'expired_products_in_stock_list',
          icon: 'bi-calendar2-x',
          description: 'expired_products_in_stock_list_desc',
          module: 'inventory',
          columns: [
            { label: 'product', key: 'product_name' },
            { label: 'sku', key: 'sku' },
            { label: 'quantity', key: 'quantity' },
            { label: 'expiration_date', key: 'expiration_date', format: 'date' }
          ]
        },
        {
          title: 'dead_stock',
          type: 'table',
          endpoint: '/v2/insights/inventory',
          dataPath: 'dead_stock_products',
          icon: 'bi-archive',
          description: 'dead_stock_desc',
          module: 'inventory',
          columns: [
            { label: 'product', key: 'product.label' },
            { label: 'sku', key: 'product.sku' },
            { label: 'quantity', key: 'quantity' }
          ]
        },
        {
          title: 'fast_moving_products',
          type: 'chart',
          endpoint: '/v2/insights/inventory',
          dataPath: 'fast_moving_products',
          chartType: 'advanced_fast_moving_products',
          icon: 'bi-lightning-charge',
          description: 'fast_moving_products_desc',
          module: 'inventory'
        },
        {
          title: 'slow_moving_products',
          type: 'chart',
          endpoint: '/v2/insights/inventory',
          dataPath: 'slow_moving_products',
          chartType: 'advanced_slow_moving_products',
          icon: 'bi-arrow-down-circle',
          description: 'slow_moving_products_desc',
          module: 'inventory'
        },
        {
          title: 'stock_movement_summary',
          type: 'chart',
          endpoint: '/v2/insights/inventory',
          dataPath: 'stock_movement_summary',
          chartType: 'stock_movement_summary',
          icon: 'bi-diagram-3',
          description: 'stock_movement_summary_desc',
          module: 'inventory'
        },
        {
          title: 'stock_by_category',
          type: 'chart',
          endpoint: '/v2/insights/inventory',
          dataPath: 'stock_by_category',
          chartType: 'inventory_stock_by_category',
          icon: 'bi-pie-chart',
          description: 'stock_by_category_desc',
          module: 'inventory'
        },
        {
          title: 'stock_by_warehouse',
          type: 'chart',
          endpoint: '/v2/insights/inventory',
          dataPath: 'stock_by_warehouse',
          chartType: 'inventory_stock_by_warehouse',
          icon: 'bi-building',
          description: 'stock_by_warehouse_desc',
          module: 'inventory'
        },

        {
          title: 'top_selling_products',
          type: 'chart',
          endpoint: '/v2/analytics?type=top-selling-products',
          chartType: 'top_selling_products',
          icon: 'bi-trophy',
          description: 'top_selling_products_desc',
          module: 'inventory'
        },
        {
          title: 'product_profitability',
          type: 'table',
          endpoint: '/v2/analytics?type=product-profitability',
          icon: 'bi-bar-chart-steps',
          description: 'product_profitability_desc',
          module: 'inventory',
          columns: [
            { label: 'product', key: 'product_label' },
            { label: 'sales_quantity', key: 'sales_quantity' },
            { label: 'sales', key: 'total_sales', format: 'currency' },
            { label: 'purchase_quantity', key: 'purchase_quantity' },
            { label: 'purchases', key: 'total_purchases', format: 'currency' },
            { label: 'profit', key: 'profit', format: 'currency' },
            { label: 'profit_margin', key: 'profit_percentage', format: 'percentage' }
          ]
        },

        // Profit & Margin Insights
        {
          title: 'gross_profit',
          type: 'number',
          endpoint: '/v2/insights/profit',
          dataPath: 'gross_profit',
          icon: 'bi-cash-coin',
          description: 'gross_profit_desc',
          format: 'currency',
          module: 'profit',
          color: '#198754'
        },
        {
          title: 'profit_margin',
          type: 'number',
          endpoint: '/v2/insights/profit',
          dataPath: 'margin_percentage',
          icon: 'bi-percent',
          description: 'profit_margin_desc',
          format: 'percentage',
          module: 'profit',
          color: '#0d6efd'
        },
        {
          title: 'discount_impact',
          type: 'number',
          endpoint: '/v2/insights/profit',
          dataPath: 'discount_impact.profit_lost_to_discounts',
          icon: 'bi-tag',
          description: 'discount_impact_desc',
          format: 'currency',
          module: 'profit',
          color: '#dc3545'
        },
        {
          title: 'profit_margin_per_product',
          type: 'chart',
          endpoint: '/v2/insights/profit',
          dataPath: 'profit_by_product',
          chartType: 'advanced_profit_by_product',
          icon: 'bi-bar-chart-steps',
          description: 'profit_margin_per_product_desc',
          module: 'profit'
        },
        {
          title: 'profit_margin_by_category',
          type: 'chart',
          endpoint: '/v2/insights/profit',
          dataPath: 'profit_by_category',
          chartType: 'advanced_profit_by_category',
          icon: 'bi-pie-chart',
          description: 'profit_margin_by_category_desc',
          module: 'profit'
        },
        {
          title: 'low_margin_products',
          type: 'table',
          endpoint: '/v2/insights/profit',
          dataPath: 'low_margin_products',
          icon: 'bi-arrow-down-circle',
          description: 'low_margin_products_desc',
          module: 'profit',
          columns: [
            { label: 'product', key: 'product_name' },
            { label: 'revenue', key: 'revenue', format: 'currency' },
            { label: 'profit', key: 'profit', format: 'currency' },
            { label: 'profit_margin', key: 'margin_percentage', format: 'percentage' }
          ]
        },
        {
          title: 'best_seller_profit_comparison',
          type: 'table',
          endpoint: '/v2/insights/profit',
          dataPath: 'best_seller_profit_comparison',
          transform: 'best_seller_profit_comparison',
          icon: 'bi-trophy',
          description: 'best_seller_profit_comparison_desc',
          module: 'profit',
          columns: [
            { label: 'insight', key: 'label' },
            { label: 'product', key: 'product_name' },
            { label: 'quantity', key: 'total_quantity' },
            { label: 'profit', key: 'profit', format: 'currency' }
          ]
        },
        
        // Sales Insights
        {
          title: 'sales_revenue_breakdown',
          type: 'sales_revenue_breakdown',
          endpoint: '/v2/insights/sales',
          dataPath: 'overview.revenue_breakdown',
          icon: 'bi-cash-stack',
          description: 'sales_revenue_breakdown_desc',
          module: 'sales',
          color: '#0d6efd',
          compare: false
        },
        {
          title: 'total_sales',
          type: 'number',
          endpoint: '/v2/insights/sales',
          dataPath: 'overview.total_sales',
          icon: 'bi-cash-stack',
          description: 'total_sales_desc',
          format: 'currency',
          module: 'sales',
          color: '#198754'
        },
        {
          title: 'payment_sales_summary',
          type: 'payment_sales_summary',
          endpoint: '/v2/insights/payment-sales-summary',
          icon: 'bi-wallet2',
          description: 'payment_sales_summary_desc',
          module: 'sales',
          color: '#198754',
          compare: false
        },
        {
          title: 'average_order_value',
          type: 'number',
          endpoint: '/v2/insights/sales',
          dataPath: 'overview.average_order_value',
          icon: 'bi-receipt',
          description: 'average_order_value_desc',
          format: 'currency',
          module: 'sales',
          color: '#fd7e14'
        },
        {
          title: 'sales_evolution',
          type: 'chart',
          endpoint: '/v2/insights/sales',
          dataPath: 'sales_trend',
          chartType: 'advanced_sales_trend',
          icon: 'bi-graph-up-arrow',
          description: 'sales_evolution_desc',
          module: 'sales'
        },
        {
          title: 'best_selling_products',
          type: 'chart',
          endpoint: '/v2/insights/sales',
          dataPath: 'top_products',
          chartType: 'advanced_best_selling_products',
          icon: 'bi-trophy',
          description: 'best_selling_products_desc',
          module: 'sales'
        },
        {
          title: 'worst_selling_products',
          type: 'chart',
          endpoint: '/v2/insights/sales',
          dataPath: 'slow_moving_products',
          chartType: 'advanced_worst_selling_products',
          icon: 'bi-arrow-down-circle',
          description: 'worst_selling_products_desc',
          module: 'sales'
        },
        {
          title: 'sales_by_client',
          type: 'chart',
          endpoint: '/v2/insights/sales',
          dataPath: 'sales_by_client',
          chartType: 'advanced_sales_by_client',
          icon: 'bi-person-lines-fill',
          description: 'sales_by_client_desc',
          module: 'sales'
        },
        {
          title: 'sales_by_payment',
          type: 'chart',
          endpoint: '/v2/insights/sales',
          dataPath: 'sales_by_payment_method',
          chartType: 'advanced_sales_by_payment_method',
          icon: 'bi-credit-card',
          description: 'sales_by_payment_desc',
          module: 'sales'
        },
        { 
          title: 'total_invoices', 
          type: 'number', 
          endpoint: '/insights/total-invoices', 
          dataKey: 'count', 
          icon: 'bi-receipt', 
          description: 'total_invoices_desc',
          module: 'sales'
        },
        { 
          title: 'total_quotes', 
          type: 'number', 
          endpoint: '/insights/total-quotes', 
          dataKey: 'count', 
          icon: 'bi-file-text', 
          description: 'total_quotes_desc',
          module: 'sales'
        },
        {
          title: 'revenue_trend',
          type: 'chart',
          endpoint: '/v2/analytics?type=revenue-trend-over-time',
          chartType: 'revenue_trend',
          icon: 'bi-graph-up',
          description: 'revenue_trend_desc',
          module: 'sales'
        },
        {
          title: 'monthly_revenue',
          type: 'chart',
          endpoint: '/v2/analytics?type=monthly-revenue',
          chartType: 'monthly_revenue',
          icon: 'bi-bar-chart',
          description: 'monthly_revenue_desc',
          module: 'sales'
        },
        {
          title: 'top_customers',
          type: 'chart',
          endpoint: '/v2/analytics?type=top-customers',
          chartType: 'top_customers',
          icon: 'bi-people-fill',
          description: 'top_customers_desc',
          module: 'sales'
        },
        {
          title: 'sales_by_category',
          type: 'chart',
          endpoint: '/v2/analytics?type=sales-by-category',
          chartType: 'sales_by_category',
          icon: 'bi-pie-chart-fill',
          description: 'sales_by_category_desc',
          module: 'sales'
        },
        {
          title: 'quotes_overview',
          type: 'quotes_stats',
          endpoint: '/v2/analytics?type=quotes-overview',
          icon: 'bi-file-earmark-bar-graph',
          description: 'quotes_overview_desc',
          module: 'sales',
          isAnalytics: true
        },
        {
          title: 'quotes_status_distribution',
          type: 'chart',
          endpoint: '/v2/analytics?type=quotes-status-distribution',
          chartType: 'quotes_status_distribution',
          icon: 'bi-pie-chart',
          description: 'quotes_status_distribution_desc',
          module: 'sales'
        },
        {
          title: 'invoices_overview',
          type: 'invoices_stats',
          endpoint: '/v2/analytics?type=invoices-overview',
          icon: 'bi-receipt-cutoff',
          description: 'invoices_overview_desc',
          module: 'sales',
          isAnalytics: true
        },
        {
          title: 'invoices_status_distribution',
          type: 'chart',
          endpoint: '/v2/analytics?type=invoices-status-distribution',
          chartType: 'invoices_status_distribution',
          icon: 'bi-pie-chart-fill',
          description: 'invoices_status_distribution_desc',
          module: 'sales'
        },
        {
          title: 'invoices_amount_distribution',
          type: 'chart',
          endpoint: '/v2/analytics?type=invoices-amount-distribution',
          chartType: 'invoices_amount_distribution',
          icon: 'bi-pie-chart-fill',
          description: 'invoices_amount_distribution_desc',
          module: 'sales'
        },

        // Lead Insights
        {
          title: 'total_leads',
          type: 'number',
          endpoint: '/v2/insights/leads',
          dataPath: 'total_leads',
          icon: 'bi-person-lines-fill',
          description: 'total_leads_desc',
          module: 'leads',
          color: '#0d6efd'
        },
        {
          title: 'leads_by_status',
          type: 'chart',
          endpoint: '/v2/insights/leads',
          dataPath: 'leads_by_status',
          chartType: 'advanced_leads_by_status',
          icon: 'bi-pie-chart-fill',
          description: 'leads_by_status_desc',
          module: 'leads'
        },
        {
          title: 'leads_by_assigned_person',
          type: 'chart',
          endpoint: '/v2/insights/leads',
          dataPath: 'leads_by_assigned_person',
          chartType: 'advanced_leads_by_assigned_person',
          icon: 'bi-person-check-fill',
          description: 'leads_by_assigned_person_desc',
          module: 'leads'
        },
        
        // Client & Supplier Insights
        { 
          title: 'total_clients', 
          type: 'number', 
          endpoint: '/insights/total-clients', 
          dataKey: 'count', 
          icon: 'bi-people', 
          description: 'total_clients_desc',
          module: 'partners'
        },
        { 
          title: 'total_suppliers', 
          type: 'number', 
          endpoint: '/insights/total-suppliers', 
          dataKey: 'count', 
          icon: 'bi-truck', 
          description: 'total_suppliers_desc',
          module: 'partners'
        },
        {
          title: 'active_clients',
          type: 'number',
          endpoint: '/v2/insights/clients',
          dataPath: 'active_clients',
          icon: 'bi-person-check',
          description: 'active_clients_desc',
          module: 'partners',
          color: '#198754'
        },
        {
          title: 'repeat_customer_rate',
          type: 'number',
          endpoint: '/v2/insights/clients',
          dataPath: 'repeat_customer_rate',
          icon: 'bi-arrow-repeat',
          description: 'repeat_customer_rate_desc',
          format: 'percentage',
          module: 'partners',
          color: '#6f42c1'
        },
        {
          title: 'average_revenue_per_client',
          type: 'number',
          endpoint: '/v2/insights/clients',
          dataPath: 'average_revenue_per_client',
          icon: 'bi-person-vcard',
          description: 'average_revenue_per_client_desc',
          format: 'currency',
          module: 'partners',
          color: '#0d6efd'
        },
        {
          title: 'client_purchase_frequency',
          type: 'number',
          endpoint: '/v2/insights/clients',
          dataPath: 'purchase_frequency.frequency',
          icon: 'bi-calendar2-check',
          description: 'client_purchase_frequency_desc',
          module: 'partners',
          color: '#fd7e14'
        },
        {
          title: 'top_clients_by_revenue',
          type: 'chart',
          endpoint: '/v2/insights/clients',
          dataPath: 'top_clients_by_revenue',
          chartType: 'advanced_clients_by_revenue',
          icon: 'bi-trophy',
          description: 'top_clients_by_revenue_desc',
          module: 'partners'
        },
        {
          title: 'inactive_clients',
          type: 'table',
          endpoint: '/v2/insights/clients',
          dataPath: 'inactive_clients',
          icon: 'bi-person-exclamation',
          description: 'inactive_clients_desc',
          module: 'partners',
          columns: [
            { label: 'client', key: 'name' },
            { label: 'email', key: 'email' },
            { label: 'phone', key: 'phone' },
            { label: 'last_purchase', key: 'last_purchase_date', format: 'date' },
            { label: 'lifetime_value', key: 'lifetime_value', format: 'currency' }
          ]
        },
        
        // Purchase Insights
        { 
          title: 'total_orders', 
          type: 'number', 
          endpoint: '/insights/total-orders', 
          dataKey: 'count', 
          icon: 'bi-cart', 
          description: 'total_orders_desc',
          module: 'purchases'
        },
        { 
          title: 'total_purchases', 
          type: 'number', 
          endpoint: '/insights/total-purchases', 
          dataKey: 'count', 
          icon: 'bi-bag', 
          description: 'total_purchases_desc',
          module: 'purchases'
        },
        {
          title: 'purchase_spend',
          type: 'number',
          endpoint: '/v2/insights/purchases',
          dataPath: 'overview.total_purchases',
          icon: 'bi-bag-check',
          description: 'purchase_spend_desc',
          format: 'currency',
          module: 'purchases'
        },
        {
          title: 'purchases_by_supplier',
          type: 'chart',
          endpoint: '/v2/insights/purchases',
          dataPath: 'purchases_by_supplier',
          chartType: 'advanced_purchases_by_supplier',
          icon: 'bi-truck',
          description: 'purchases_by_supplier_desc',
          module: 'purchases'
        },
        {
          title: 'supplier_payment_status',
          type: 'chart',
          endpoint: '/v2/insights/purchases',
          dataPath: 'supplier_payment_status',
          chartType: 'advanced_supplier_payment_status',
          icon: 'bi-wallet2',
          description: 'supplier_payment_status_desc',
          module: 'purchases'
        },
        {
          title: 'most_purchased_products',
          type: 'chart',
          endpoint: '/v2/insights/purchases',
          dataPath: 'purchases_by_product',
          chartType: 'advanced_most_purchased_products',
          icon: 'bi-box-seam',
          description: 'most_purchased_products_desc',
          module: 'purchases'
        },
        {
          title: 'purchase_price_changes',
          type: 'table',
          endpoint: '/v2/insights/purchases',
          dataPath: 'purchase_price_changes',
          icon: 'bi-graph-up-arrow',
          description: 'purchase_price_changes_desc',
          module: 'purchases',
          columns: [
            { label: 'product', key: 'product_name' },
            { label: 'supplier', key: 'supplier_name' },
            { label: 'previous_price', key: 'previous_price', format: 'currency' },
            { label: 'latest_price', key: 'latest_price', format: 'currency' },
            { label: 'change', key: 'change_percentage', format: 'percentage' }
          ]
        },
        {
          title: 'supplier_delivery_performance',
          type: 'table',
          endpoint: '/v2/insights/purchases',
          dataPath: 'supplier_delivery_performance',
          icon: 'bi-clipboard-check',
          description: 'supplier_delivery_performance_desc',
          module: 'purchases',
          columns: [
            { label: 'supplier', key: 'supplier_name' },
            { label: 'total_receptions', key: 'total_receptions' },
            { label: 'completed', key: 'completed_receptions' },
            { label: 'pending', key: 'pending_receptions' },
            { label: 'completion_rate', key: 'completion_rate', format: 'percentage' }
          ]
        },
        
        // Logistics & Operations
        { 
          title: 'total_deliveries', 
          type: 'number', 
          endpoint: '/insights/total-deliveries', 
          dataKey: 'count', 
          icon: 'bi-truck', 
          description: 'total_deliveries_desc',
          module: 'logistics'
        },
        { 
          title: 'total_returns', 
          type: 'number', 
          endpoint: '/insights/total-returns', 
          dataKey: 'count', 
          icon: 'bi-arrow-return-left', 
          description: 'total_returns_desc',
          module: 'logistics'
        },
        { 
          title: 'total_pos_sales', 
          type: 'number', 
          endpoint: '/v2/insights/pos',
          dataPath: 'total_pos_sales',
          icon: 'bi-shop', 
          description: 'total_pos_sales_desc',
          format: 'currency',
          module: 'pos'
        },
        {
          title: 'pos_canceled_orders',
          type: 'number',
          endpoint: '/v2/insights/pos',
          dataPath: 'refund_count',
          icon: 'bi-x-circle',
          description: 'pos_canceled_orders_desc',
          module: 'pos',
          color: '#dc3545',
          compare: false
        },
        {
          title: 'pos_total_orders',
          type: 'number',
          endpoint: '/v2/insights/pos',
          dataPath: 'pos_count',
          icon: 'bi-receipt-cutoff',
          description: 'pos_total_orders_desc',
          module: 'pos',
          color: '#0d6efd',
          compare: false
        },
        {
          title: 'daily_sales_trend',
          type: 'chart',
          endpoint: '/v2/insights/pos',
          dataPath: 'daily_sales_trend',
          chartType: 'advanced_pos_daily_sales_trend',
          icon: 'bi-graph-up-arrow',
          description: 'pos_daily_sales_trend_desc',
          module: 'pos'
        },
        {
          title: 'sales_by_product',
          type: 'chart',
          endpoint: '/v2/insights/pos',
          dataPath: 'sales_by_product',
          chartType: 'advanced_pos_sales_by_product',
          icon: 'bi-box-seam',
          description: 'pos_top_selling_products_desc',
          module: 'pos'
        },
        {
          title: 'orders_by_payment_method',
          type: 'chart',
          endpoint: '/v2/insights/pos',
          dataPath: 'orders_by_payment_method',
          chartType: 'advanced_pos_orders_by_payment_method',
          icon: 'bi-credit-card',
          description: 'pos_sales_by_payment_desc',
          module: 'pos'
        },
        {
          title: 'pos_sales_by_cashier',
          type: 'chart',
          endpoint: '/v2/insights/pos',
          dataPath: 'sales_by_cashier',
          chartType: 'advanced_pos_sales_by_cashier',
          icon: 'bi-person-badge',
          description: 'pos_sales_by_cashier_desc',
          module: 'pos'
        },
        {
          title: 'pos_sales_by_shift',
          type: 'chart',
          endpoint: '/v2/insights/pos',
          dataPath: 'sales_by_cash_register',
          chartType: 'advanced_pos_sales_by_register',
          icon: 'bi-window',
          description: 'pos_sales_by_shift_desc',
          module: 'pos'
        },
        {
          title: 'cash_register_difference',
          type: 'table',
          endpoint: '/v2/insights/pos',
          dataPath: 'cash_register_differences',
          icon: 'bi-calculator',
          description: 'cash_register_difference_desc',
          module: 'pos',
          columns: [
            { label: 'cash_register', key: 'reference' },
            { label: 'cashier', key: 'cashier_name' },
            { label: 'opening_balance', key: 'opening_balance', format: 'currency' },
            { label: 'closing_balance', key: 'closing_balance', format: 'currency' },
            { label: 'cash_difference', key: 'cash_difference', format: 'currency' }
          ]
        },
        {
          title: 'pos_refunds_returns',
          type: 'table',
          endpoint: '/v2/insights/pos',
          dataPath: 'refunds',
          icon: 'bi-arrow-counterclockwise',
          description: 'pos_refunds_returns_desc',
          module: 'pos',
          columns: [
            { label: 'reference', key: 'reference' },
            { label: 'cashier', key: 'cashier_name' },
            { label: 'payment_method', key: 'payment_method' },
            { label: 'amount', key: 'total_amount', format: 'currency' },
            { label: 'date', key: 'sale_date', format: 'date' }
          ]
        },
        {
          title: 'pos_active_hours',
          type: 'chart',
          endpoint: '/v2/insights/pos',
          dataPath: 'peak_hours',
          chartType: 'advanced_pos_active_hours',
          icon: 'bi-clock',
          description: 'pos_active_hours_desc',
          module: 'pos'
        },
        {
          title: 'average_ticket_size',
          type: 'number',
          endpoint: '/v2/insights/pos',
          dataPath: 'average_ticket_size',
          icon: 'bi-receipt',
          description: 'average_ticket_size_desc',
          format: 'currency',
          module: 'pos'
        },
        { 
          title: 'total_transactions', 
          type: 'number', 
          endpoint: '/insights/total-transactions', 
          dataKey: 'count', 
          icon: 'bi-arrow-left-right', 
          description: 'total_transactions_desc',
          module: 'financial'
        },
        
        // Financial Insights
        { 
          title: 'invoice_total_sum', 
          type: 'number', 
          endpoint: '/insights/invoice-total-sum', 
          dataKey: 'count', 
          icon: 'bi-cash-stack', 
          description: 'invoice_total_sum_desc',
          format: 'currency',
          module: 'financial'
        },
        { 
          title: 'paid_invoiced_amount', 
          type: 'number', 
          endpoint: '/insights/paid-invoiced-amount', 
          dataKey: 'count', 
          icon: 'bi-check-all', 
          description: 'paid_invoiced_amount_desc',
          format: 'currency',
          module: 'financial'
        },
        { 
          title: 'pending_invoiced_amount', 
          type: 'number', 
          endpoint: '/insights/pending-invoiced-amount', 
          dataKey: 'count', 
          icon: 'bi-clock-history', 
          description: 'pending_invoiced_amount_desc',
          format: 'currency',
          module: 'financial'
        },
        { 
          title: 'invoice_vat_sum', 
          type: 'number', 
          endpoint: '/insights/invoice-vat-sum', 
          dataKey: 'count', 
          icon: 'bi-percent', 
          description: 'invoice_vat_sum_desc',
          format: 'currency',
          module: 'financial'
        },
        { 
          title: 'invoice_discount_sum', 
          type: 'number', 
          endpoint: '/insights/invoice-discount-sum', 
          dataKey: 'count', 
          icon: 'bi-tag', 
          description: 'invoice_discount_sum_desc',
          format: 'currency',
          module: 'financial'
        },
        { 
          title: 'expense_sum', 
          type: 'number', 
          endpoint: '/v2/insights/financial',
          dataPath: 'cash_out',
          icon: 'bi-dash-circle', 
          description: 'expense_sum_desc',
          format: 'currency',
          module: 'financial'
        },
        { 
          title: 'income_sum', 
          type: 'number', 
          endpoint: '/v2/insights/financial',
          dataPath: 'cash_in',
          icon: 'bi-plus-circle', 
          description: 'income_sum_desc',
          format: 'currency',
          module: 'financial'
        },
        {
          title: 'income_by_category',
          type: 'chart',
          endpoint: '/v2/insights/financial',
          dataPath: 'income_by_category',
          chartType: 'financial_income_by_category',
          icon: 'bi-pie-chart-fill',
          description: 'income_by_category_desc',
          module: 'financial'
        },
        {
          title: 'expense_by_category',
          type: 'chart',
          endpoint: '/v2/insights/financial',
          dataPath: 'expense_by_category',
          chartType: 'financial_expense_by_category',
          icon: 'bi-pie-chart',
          description: 'expense_by_category_desc',
          module: 'financial'
        },
        {
          title: 'total_customers_balance',
          type: 'number',
          endpoint: '/insights/total-customers-balance',
          dataKey: 'count',
          icon: 'bi-wallet2',
          description: 'total_customers_balance_desc',
          format: 'currency',
          module: 'financial'
        },
        {
          title: 'net_income',
          type: 'number',
          endpoint: '/v2/insights/financial',
          dataPath: 'net_cash_flow',
          icon: 'bi-cash-coin',
          description: 'net_income_desc',
          format: 'currency',
          module: 'financial'
        },
        
        // Invoice Status
        { 
          title: 'paid_invoices', 
          type: 'number', 
          endpoint: '/insights/paid-invoices', 
          dataKey: 'count', 
          icon: 'bi-check-circle', 
          description: 'paid_invoices_desc',
          module: 'invoice_status'
        },
        { 
          title: 'pending_invoices', 
          type: 'number', 
          endpoint: '/insights/pending-invoices', 
          dataKey: 'count', 
          icon: 'bi-hourglass-split', 
          description: 'pending_invoices_desc',
          module: 'invoice_status'
        },
        { 
          title: 'canceled_invoices', 
          type: 'number', 
          endpoint: '/insights/canceled-invoices', 
          dataKey: 'count', 
          icon: 'bi-x-circle', 
          description: 'canceled_invoices_desc',
          module: 'invoice_status'
        },
        { 
          title: 'overdue_invoices', 
          type: 'number', 
          endpoint: '/insights/overdue-invoices', 
          dataKey: 'count', 
          icon: 'bi-calendar-x', 
          description: 'overdue_invoices_desc',
          module: 'invoice_status'
        },
        { 
          title: 'invoices_status_trend', 
          type: 'chart', 
          endpoint: '/v2/analytics?type=invoices-status-trend', 
          chartType: 'invoices_status_trend', 
          icon: 'bi-graph-up-arrow', 
          description: 'invoices_status_trend_desc', 
          module: 'invoice_status'
        }
      ],
      modalInstance: null,
      hasUnsavedChanges: false,
      originalWidgets: null,
      start: moment().subtract(3, 'month').format('YYYY-MM-DD'),
      end: moment().format('YYYY-MM-DD'),
      dateRangeOption: '',
      range: [],
      widgetSearchQuery: '',
      selectedWidgetModule: 'all',
      widgetModuleDefinitions: [
        { key: 'all', label: 'all_modules', icon: 'bi-grid' },
        { key: 'inventory', label: 'inventory', icon: 'bi-box-seam' },
        { key: 'sales', label: 'sales', icon: 'bi-receipt' },
        { key: 'purchases', label: 'purchases', icon: 'bi-cart3' },
        { key: 'profit', label: 'profit', icon: 'bi-graph-up-arrow' },
        { key: 'pos', label: 'pos', icon: 'bi-shop' },
        { key: 'financial', label: 'finance', icon: 'bi-wallet2' },
        { key: 'leads', label: 'leads', icon: 'bi-person-lines-fill' },
        { key: 'partners', label: 'clients', icon: 'bi-people' },
        { key: 'logistics', label: 'logistics', icon: 'bi-truck' },
        { key: 'invoice_status', label: 'invoice_status', icon: 'bi-file-earmark-check' }
      ],
      textWidgetConfig: {
        title: '',
        content: ''
      },
      textWidgetModalInstance: null,
      
      // Theme colors for widgets
      themeColors: ['#0d6efd', '#198754', '#dc3545', '#ffc107', '#0dcaf0', '#6f42c1', '#fd7e14', '#20c997'],
      
      // Dashboard Presets - Using only verified existing endpoints
      dashboardPresets: {
        home: [
          { title: 'sales_revenue_breakdown', type: 'sales_revenue_breakdown', endpoint: '/v2/insights/sales', dataPath: 'overview.revenue_breakdown', icon: 'bi-cash-stack', description: 'sales_revenue_breakdown_desc', module: 'sales', width: 'col-md-6 col-lg-6', color: '#0d6efd', compare: false },
          { title: 'income_sum', type: 'number', endpoint: '/v2/insights/financial', dataPath: 'cash_in', icon: 'bi-plus-circle', format: 'currency', module: 'financial', width: 'col-md-3 col-lg-3', color: '#198754' },
          { title: 'expense_sum', type: 'number', endpoint: '/v2/insights/financial', dataPath: 'cash_out', icon: 'bi-dash-circle', format: 'currency', module: 'financial', width: 'col-md-3 col-lg-3', color: '#dc3545' },
          { title: 'top_clients_by_revenue', type: 'chart', endpoint: '/v2/insights/clients', dataPath: 'top_clients_by_revenue', chartType: 'advanced_clients_by_revenue', icon: 'bi-trophy', module: 'partners', width: 'col-md-6 col-lg-6' },
          { title: 'best_selling_products', type: 'chart', endpoint: '/v2/insights/sales', dataPath: 'top_products', chartType: 'advanced_best_selling_products', icon: 'bi-trophy', module: 'sales', width: 'col-md-6 col-lg-6' },
          { title: 'invoices_status_distribution', type: 'chart', endpoint: '/v2/analytics?type=invoices-status-distribution', chartType: 'invoices_status_distribution', icon: 'bi-pie-chart-fill', module: 'sales', width: 'col-md-6 col-lg-6' },
          { title: 'sales_by_category', type: 'chart', endpoint: '/v2/analytics?type=sales-by-category', chartType: 'sales_by_category', icon: 'bi-pie-chart-fill', module: 'sales', width: 'col-md-6 col-lg-6' },
          { title: 'most_purchased_products', type: 'chart', endpoint: '/v2/insights/purchases', dataPath: 'purchases_by_product', chartType: 'advanced_most_purchased_products', icon: 'bi-box-seam', module: 'purchases', width: 'col-md-6 col-lg-6' },
          { title: 'purchases_by_supplier', type: 'chart', endpoint: '/v2/insights/purchases', dataPath: 'purchases_by_supplier', chartType: 'advanced_purchases_by_supplier', icon: 'bi-truck', module: 'purchases', width: 'col-md-6 col-lg-6' }
        ],
        sales: [
          { title: 'payment_sales_summary', type: 'payment_sales_summary', endpoint: '/v2/insights/payment-sales-summary', icon: 'bi-wallet2', description: 'payment_sales_summary_desc', module: 'sales', width: 'col-md-6 col-lg-6', color: '#198754', compare: false },
          { title: 'sales_revenue_breakdown', type: 'sales_revenue_breakdown', endpoint: '/v2/insights/sales', dataPath: 'overview.revenue_breakdown', icon: 'bi-cash-stack', description: 'sales_revenue_breakdown_desc', module: 'sales', width: 'col-12', color: '#0d6efd', compare: false },
          { title: 'best_selling_products', type: 'chart', endpoint: '/v2/insights/sales', dataPath: 'top_products', chartType: 'advanced_best_selling_products', icon: 'bi-trophy', module: 'sales', width: 'col-md-6 col-lg-6' },
          { title: 'sales_by_client', type: 'chart', endpoint: '/v2/insights/sales', dataPath: 'sales_by_client', chartType: 'advanced_sales_by_client', icon: 'bi-person-lines-fill', module: 'sales', width: 'col-md-6 col-lg-6' },
          { title: 'sales_evolution', type: 'chart', endpoint: '/v2/insights/sales', dataPath: 'sales_trend', chartType: 'advanced_sales_trend', icon: 'bi-graph-up-arrow', module: 'sales', width: 'col-12' },
          { title: 'sales_by_category', type: 'chart', endpoint: '/v2/analytics?type=sales-by-category', chartType: 'sales_by_category', icon: 'bi-pie-chart-fill', module: 'sales', width: 'col-md-6 col-lg-6' },
          { title: 'sales_by_payment', type: 'chart', endpoint: '/v2/insights/sales', dataPath: 'sales_by_payment_method', chartType: 'advanced_sales_by_payment_method', icon: 'bi-credit-card', module: 'sales', width: 'col-md-6 col-lg-6' }
        ],
        inventory: [
          { title: 'total_products', type: 'number', endpoint: '/insights/total-products', dataKey: 'count', icon: 'bi-box-seam', module: 'inventory', width: 'col-md-3 col-lg-3', color: '#0d6efd' },
          { title: 'stock_value', type: 'number', endpoint: '/v2/insights/inventory', dataPath: 'current_stock_value', icon: 'bi-cash-stack', format: 'currency', module: 'inventory', width: 'col-md-3 col-lg-3', color: '#198754', compare: false },
          { title: 'potential_selling_value', type: 'number', endpoint: '/v2/insights/inventory', dataPath: 'potential_selling_value', icon: 'bi-currency-exchange', format: 'currency', module: 'inventory', width: 'col-md-3 col-lg-3', color: '#0d6efd', compare: false },
          { title: 'potential_profit_in_stock', type: 'number', endpoint: '/v2/insights/inventory', dataPath: 'potential_profit_in_stock', icon: 'bi-graph-up-arrow', format: 'currency', module: 'inventory', width: 'col-md-3 col-lg-3', color: '#20c997', compare: false },
          { title: 'low_stock_products', type: 'number', endpoint: '/v2/insights/inventory', dataPath: 'low_stock_products', icon: 'bi-exclamation-circle', module: 'inventory', width: 'col-md-3 col-lg-3', color: '#ffc107', compare: false },
          { title: 'out_of_stock_products', type: 'number', endpoint: '/v2/insights/inventory', dataPath: 'out_of_stock_products', icon: 'bi-x-octagon', module: 'inventory', width: 'col-md-3 col-lg-3', color: '#dc3545', compare: false },
          { title: 'in_stock_products', type: 'number', endpoint: '/v2/insights/inventory', dataPath: 'in_stock_products', icon: 'bi-check-circle', module: 'inventory', width: 'col-md-3 col-lg-3', color: '#198754', compare: false },
          { title: 'fast_moving_products', type: 'chart', endpoint: '/v2/insights/inventory', dataPath: 'fast_moving_products', chartType: 'advanced_fast_moving_products', icon: 'bi-lightning-charge', module: 'inventory', width: 'col-md-6 col-lg-6' },
          { title: 'slow_moving_products', type: 'chart', endpoint: '/v2/insights/inventory', dataPath: 'slow_moving_products', chartType: 'advanced_slow_moving_products', icon: 'bi-arrow-down-circle', module: 'inventory', width: 'col-md-6 col-lg-6' },
          { title: 'stock_movement_summary', type: 'chart', endpoint: '/v2/insights/inventory', dataPath: 'stock_movement_summary', chartType: 'stock_movement_summary', icon: 'bi-diagram-3', module: 'inventory', width: 'col-md-6 col-lg-6' },
          { title: 'stock_by_category', type: 'chart', endpoint: '/v2/insights/inventory', dataPath: 'stock_by_category', chartType: 'inventory_stock_by_category', icon: 'bi-pie-chart', module: 'inventory', width: 'col-md-6 col-lg-6' },
          { title: 'stock_by_warehouse', type: 'chart', endpoint: '/v2/insights/inventory', dataPath: 'stock_by_warehouse', chartType: 'inventory_stock_by_warehouse', icon: 'bi-building', module: 'inventory', width: 'col-md-6 col-lg-6' },
          { title: 'low_stock_product_list', type: 'table', endpoint: '/v2/insights/inventory', dataPath: 'low_stock_product_list', icon: 'bi-list-check', module: 'inventory', width: 'col-md-6 col-lg-6', columns: [{ label: 'product', key: 'product_name' }, { label: 'sku', key: 'sku' }, { label: 'quantity', key: 'quantity' }, { label: 'min_quantity', key: 'min_quantity' }] },
          { title: 'products_close_to_minimum_stock_list', type: 'table', endpoint: '/v2/insights/inventory', dataPath: 'products_close_to_minimum_stock_list', icon: 'bi-speedometer', module: 'inventory', width: 'col-md-6 col-lg-6', columns: [{ label: 'product', key: 'product_name' }, { label: 'sku', key: 'sku' }, { label: 'quantity', key: 'quantity' }, { label: 'min_quantity', key: 'min_quantity' }] },
          { title: 'out_of_stock_product_list', type: 'table', endpoint: '/v2/insights/inventory', dataPath: 'out_of_stock_product_list', icon: 'bi-clipboard-x', module: 'inventory', width: 'col-md-6 col-lg-6', columns: [{ label: 'product', key: 'product_name' }, { label: 'sku', key: 'sku' }, { label: 'quantity', key: 'quantity' }] },
          { title: 'dead_stock', type: 'table', endpoint: '/v2/insights/inventory', dataPath: 'dead_stock_products', icon: 'bi-archive', module: 'inventory', width: 'col-md-6 col-lg-6', columns: [{ label: 'product', key: 'product.label' }, { label: 'sku', key: 'product.sku' }, { label: 'quantity', key: 'quantity' }] }
        ],
        purchases: [
          { title: 'purchase_spend', type: 'number', endpoint: '/v2/insights/purchases', dataPath: 'overview.total_purchases', icon: 'bi-bag-check', format: 'currency', module: 'purchases', width: 'col-md-3 col-lg-3', color: '#6f42c1' },
          { title: 'purchases_by_supplier', type: 'chart', endpoint: '/v2/insights/purchases', dataPath: 'purchases_by_supplier', chartType: 'advanced_purchases_by_supplier', icon: 'bi-truck', module: 'purchases', width: 'col-md-6 col-lg-6' },
          { title: 'supplier_payment_status', type: 'chart', endpoint: '/v2/insights/purchases', dataPath: 'supplier_payment_status', chartType: 'advanced_supplier_payment_status', icon: 'bi-wallet2', module: 'purchases', width: 'col-md-3 col-lg-3' },
          { title: 'most_purchased_products', type: 'chart', endpoint: '/v2/insights/purchases', dataPath: 'purchases_by_product', chartType: 'advanced_most_purchased_products', icon: 'bi-box-seam', module: 'purchases', width: 'col-12' },
          { title: 'purchase_price_changes', type: 'table', endpoint: '/v2/insights/purchases', dataPath: 'purchase_price_changes', icon: 'bi-graph-up-arrow', module: 'purchases', width: 'col-md-6 col-lg-6', columns: [{ label: 'product', key: 'product_name' }, { label: 'supplier', key: 'supplier_name' }, { label: 'previous_price', key: 'previous_price', format: 'currency' }, { label: 'latest_price', key: 'latest_price', format: 'currency' }, { label: 'change', key: 'change_percentage', format: 'percentage' }] },
          { title: 'supplier_delivery_performance', type: 'table', endpoint: '/v2/insights/purchases', dataPath: 'supplier_delivery_performance', icon: 'bi-clipboard-check', module: 'purchases', width: 'col-md-6 col-lg-6', columns: [{ label: 'supplier', key: 'supplier_name' }, { label: 'total_receptions', key: 'total_receptions' }, { label: 'completed', key: 'completed_receptions' }, { label: 'pending', key: 'pending_receptions' }, { label: 'completion_rate', key: 'completion_rate', format: 'percentage' }] }
        ],
        profits: [
          { title: 'gross_profit', type: 'number', endpoint: '/v2/insights/profit', dataPath: 'gross_profit', icon: 'bi-cash-coin', format: 'currency', module: 'profit', width: 'col-md-3 col-lg-3', color: '#198754' },
          { title: 'profit_margin', type: 'number', endpoint: '/v2/insights/profit', dataPath: 'margin_percentage', icon: 'bi-percent', format: 'percentage', module: 'profit', width: 'col-md-3 col-lg-3', color: '#0d6efd' },
          { title: 'discount_impact', type: 'number', endpoint: '/v2/insights/profit', dataPath: 'discount_impact.profit_lost_to_discounts', icon: 'bi-tag', format: 'currency', module: 'profit', width: 'col-md-3 col-lg-3', color: '#dc3545' },
          { title: 'profit_margin_per_product', type: 'chart', endpoint: '/v2/insights/profit', dataPath: 'profit_by_product', chartType: 'advanced_profit_by_product', icon: 'bi-bar-chart-steps', module: 'profit', width: 'col-md-6 col-lg-6' },
          { title: 'profit_margin_by_category', type: 'chart', endpoint: '/v2/insights/profit', dataPath: 'profit_by_category', chartType: 'advanced_profit_by_category', icon: 'bi-pie-chart', module: 'profit', width: 'col-md-6 col-lg-6' },
          { title: 'low_margin_products', type: 'table', endpoint: '/v2/insights/profit', dataPath: 'low_margin_products', icon: 'bi-arrow-down-circle', module: 'profit', width: 'col-md-12 col-lg-12', columns: [{ label: 'product', key: 'product_name' }, { label: 'revenue', key: 'revenue', format: 'currency' }, { label: 'profit', key: 'profit', format: 'currency' }, { label: 'profit_margin', key: 'margin_percentage', format: 'percentage' }] },
          { title: 'best_seller_profit_comparison', type: 'table', endpoint: '/v2/insights/profit', dataPath: 'best_seller_profit_comparison', transform: 'best_seller_profit_comparison', icon: 'bi-trophy', module: 'profit', width: 'col-md-12 col-lg-12', columns: [{ label: 'insight', key: 'label' }, { label: 'product', key: 'product_name' }, { label: 'quantity', key: 'total_quantity' }, { label: 'profit', key: 'profit', format: 'currency' }] }
        ],
        pos: [
          { title: 'total_pos_sales', type: 'number', endpoint: '/v2/insights/pos', dataPath: 'total_pos_sales', icon: 'bi-shop', format: 'currency', module: 'pos', width: 'col-md-3 col-lg-3', color: '#198754' },
          { title: 'pos_total_orders', type: 'number', endpoint: '/v2/insights/pos', dataPath: 'pos_count', icon: 'bi-receipt-cutoff', module: 'pos', width: 'col-md-3 col-lg-3', color: '#0d6efd', compare: false },
          { title: 'pos_canceled_orders', type: 'number', endpoint: '/v2/insights/pos', dataPath: 'refund_count', icon: 'bi-x-circle', module: 'pos', width: 'col-md-3 col-lg-3', color: '#dc3545', compare: false },
          { title: 'daily_sales_trend', type: 'chart', endpoint: '/v2/insights/pos', dataPath: 'daily_sales_trend', chartType: 'advanced_pos_daily_sales_trend', icon: 'bi-graph-up-arrow', module: 'pos', width: 'col-md-6 col-lg-6' },
          { title: 'sales_by_product', type: 'chart', endpoint: '/v2/insights/pos', dataPath: 'sales_by_product', chartType: 'advanced_pos_sales_by_product', icon: 'bi-box-seam', module: 'pos', width: 'col-md-6 col-lg-6' },
          { title: 'orders_by_payment_method', type: 'chart', endpoint: '/v2/insights/pos', dataPath: 'orders_by_payment_method', chartType: 'advanced_pos_orders_by_payment_method', icon: 'bi-credit-card', module: 'pos', width: 'col-md-6 col-lg-6' }
        ],
        financial: [
          { title: 'income_sum', type: 'number', endpoint: '/v2/insights/financial', dataPath: 'cash_in', icon: 'bi-plus-circle', format: 'currency', module: 'financial', width: 'col-md-3 col-lg-3', color: '#198754' },
          { title: 'expense_sum', type: 'number', endpoint: '/v2/insights/financial', dataPath: 'cash_out', icon: 'bi-dash-circle', format: 'currency', module: 'financial', width: 'col-md-3 col-lg-3', color: '#dc3545' },
          { title: 'net_income', type: 'number', endpoint: '/v2/insights/financial', dataPath: 'net_cash_flow', icon: 'bi-cash-coin', format: 'currency', module: 'financial', width: 'col-md-6 col-lg-6', color: '#0d6efd' },
          { title: 'income_by_category', type: 'chart', endpoint: '/v2/insights/financial', dataPath: 'income_by_category', chartType: 'financial_income_by_category', icon: 'bi-pie-chart-fill', module: 'financial', width: 'col-md-6 col-lg-6' },
          { title: 'expense_by_category', type: 'chart', endpoint: '/v2/insights/financial', dataPath: 'expense_by_category', chartType: 'financial_expense_by_category', icon: 'bi-pie-chart', module: 'financial', width: 'col-md-6 col-lg-6' }
        ]
      },
      
      // Advanced Filters
      users: [],
      warehouses: [],
      selectedUserId: '',
      selectedWarehouseId: ''
    }
  },
  computed: {
    activeTab() {
      return this.tabs.find(t => t.id === this.activeTabId) || this.tabs[0]
    },
    activeTabWidgets: {
      get() {
        return this.activeTab ? this.activeTab.widgets : []
      },
      set(val) {
        if (this.activeTab) {
          this.activeTab.widgets = val
        }
      }
    },
    // Backwards compatible getter for methods that reference this.widgets
    widgets: {
      get() {
        return this.activeTabWidgets
      },
      set(val) {
        this.activeTabWidgets = val
      }
    },
    visibleAvailableWidgets() {
      return this.availableWidgets
        .filter(widget => (
          !this.hiddenInsightTitles.includes(widget.title) &&
          this.isWidgetAllowedForEnabledModules(widget)
        ))
        .map(widget => ({
          ...widget,
          lockedByPlan: !this.isWidgetAllowedForPlan(widget)
        }))
    },
    hasAvailableWidgetsToAdd() {
      if (this.isLoadingLayout || this.activeTabWidgets.length === 0) return false
      return this.visibleAvailableWidgets.some(widget => {
        return this.isWidgetAllowedForPlan(widget) && !this.activeTabWidgets.some(w => w.title === widget.title && w.module === widget.module)
      })
    },
    unselectedWidgets() {
      const currentWidgets = this.activeTabWidgets
      return this.visibleAvailableWidgets.filter(widget => {
        return !currentWidgets.some(w => w.title === widget.title && w.module === widget.module)
      })
    },
    filteredWidgets() {
      if (!this.widgetSearchQuery) {
        return this.unselectedWidgets
      }
      
      const query = this.widgetSearchQuery.toLowerCase()
      return this.unselectedWidgets.filter(widget => {
        const title = String(this.$t(widget.title)).toLowerCase()
        const description = String(this.$t(widget.description || 'widget_description')).toLowerCase()
        return title.includes(query) || description.includes(query)
      })
    },
    groupedWidgets() {
      const groups = {}
      this.filteredWidgets.forEach(widget => {
        const module = widget.module || 'other'
        if (!groups[module]) {
          groups[module] = []
        }
        groups[module].push(widget)
      })
      return groups
    },
    recommendedWidgets() {
      if (this.widgetSearchQuery) return []

      const recommendedTitles = this.beginnerRecommendations[this.selectedWidgetModule] || []
      return recommendedTitles
        .map(title => this.unselectedWidgets.find(widget => (
          widget.title === title &&
          (this.selectedWidgetModule === 'all' || widget.module === this.selectedWidgetModule)
        )))
        .filter(widget => widget && this.isWidgetAllowedForPlan(widget))
    },
    beginnerRecommendationDescriptionKey() {
      const moduleKey = this.beginnerRecommendations[this.selectedWidgetModule]
        ? this.selectedWidgetModule
        : 'all'
      return `beginner_insights_${moduleKey}_desc`
    },
    availableWidgetModules() {
      const counts = this.unselectedWidgets.reduce((modules, widget) => {
        const module = widget.module || 'other'
        modules[module] = (modules[module] || 0) + 1
        return modules
      }, {})
      const knownModuleKeys = this.widgetModuleDefinitions.map(module => module.key)
      const modules = this.widgetModuleDefinitions
        .filter(module => module.key === 'all' || counts[module.key])
        .map(module => ({
          ...module,
          count: module.key === 'all' ? this.unselectedWidgets.length : counts[module.key]
        }))
      const unknownModules = Object.keys(counts)
        .filter(module => !knownModuleKeys.includes(module))
        .map(module => ({
          key: module,
          label: module,
          icon: 'bi-grid',
          count: counts[module]
        }))

      return [...modules, ...unknownModules]
    },
    displayedWidgetGroups() {
      const groups = this.selectedWidgetModule === 'all'
        ? this.groupedWidgets
        : { [this.selectedWidgetModule]: this.groupedWidgets[this.selectedWidgetModule] || [] }
      const recommendedKeys = new Set(
        this.recommendedWidgets.map(widget => `${widget.module}:${widget.title}`)
      )

      return Object.keys(groups).reduce((remainingGroups, moduleName) => {
        const remainingWidgets = groups[moduleName].filter(widget => (
          !recommendedKeys.has(`${widget.module}:${widget.title}`)
        ))
        if (remainingWidgets.length) {
          remainingGroups[moduleName] = remainingWidgets
        }
        return remainingGroups
      }, {})
    },
    currentPlanKey() {
      return getBusinessPlanKey(this.$store.getters.BUSINESS)
    },
    isStarterPlan() {
      return this.currentPlanKey === 'starter'
    }
  },
  async mounted() {
    //check permissions
    const advancedInsightsAccess = helper.checkAccess('view_advanced_insights')
    if(advancedInsightsAccess == -1 && !this.isStarterPlan) return this.$router.push({path : '/'})
    if(advancedInsightsAccess == -2) return this.$router.push({path : '/subscribe'})

    // Only create first-time defaults after the API confirms that no saved
    // template exists. A failed lookup must never overwrite an existing layout.
    let templateLookupSucceeded = false
    let hasSavedLayout = false

    // Load saved layout from Template
    try {
      const response = await helper.customGet(`${process.env.VUE_APP_API}/templates-by-type?type=advanced_insights`)

      templateLookupSucceeded = true
      if (response && response.data) {
        hasSavedLayout = true
        const rawTemplateData = response.data.data
        const templateData = typeof rawTemplateData === 'string' ? JSON.parse(rawTemplateData) : rawTemplateData
        
        // Check if new tabs format or old flat array format
        if (templateData && templateData.tabs && Array.isArray(templateData.tabs)) {
          // New tabs format
          this.tabs = templateData.tabs.length
            ? templateData.tabs.map(tab => ({
              ...tab,
              widgets: this.filterWidgetsForCurrentPlan(tab.widgets || []).map(w => this.createRuntimeWidget(w))
            }))
            : [{
              id: 'default',
              name: this.$t('dashboard'),
              icon: 'bi-grid-1x2',
              widgets: []
            }]
          this.activeTabId = templateData.activeTabId || (this.tabs[0] ? this.tabs[0].id : 'default')
        } else if (Array.isArray(templateData)) {
          // Old flat array format — migrate to tabs
          const defaultTab = {
            id: 'default',
            name: this.$t('dashboard'),
            icon: 'bi-grid-1x2',
            widgets: this.filterWidgetsForCurrentPlan(templateData).map(w => this.createRuntimeWidget(w))
          }
          this.tabs = [defaultTab]
          this.activeTabId = 'default'
        }
        
        if (templateData) this.refreshAllTabs()
      }
    } catch (e) {
      // The legacy local layout is checked below. Do not create or save
      // defaults here because the server lookup may have failed temporarily.
    } finally {
      // Preserve layouts from versions that stored Insights in localStorage.
      const localSaved = localStorage.getItem('advanced_insights_layout')
      if (!hasSavedLayout && localSaved) {
        try {
          const parsedWidgets = JSON.parse(localSaved)
          // Initialize runtime properties for localStorage widgets too (old format)
          const defaultTab = {
            id: 'default',
            name: this.$t('dashboard'),
            icon: 'bi-grid-1x2',
            widgets: this.filterWidgetsForCurrentPlan(parsedWidgets).map(w => this.createRuntimeWidget(w))
          }
          this.tabs = [defaultTab]
          this.activeTabId = 'default'
          hasSavedLayout = true
          this.refreshAllTabs()
        } catch (err) {
          // Invalid legacy data should not block a confirmed first-time setup.
        }
      }

      if (templateLookupSucceeded && !hasSavedLayout) {
        const defaultTabs = this.firstTimeDefaultTabs()
        if (defaultTabs.length) {
          this.tabs = defaultTabs
          this.activeTabId = this.tabs[0].id
          this.refreshAllTabs()
          await this.saveLayout({ silent: true })
        } else {
          this.tabs = [{
            id: 'default',
            name: this.$t('dashboard'),
            icon: 'bi-grid-1x2',
            widgets: []
          }]
          this.activeTabId = 'default'
        }
      }
      this.isLoadingLayout = false
    }
     
    // Initialize modals
    // eslint-disable-next-line no-undef
    this.modalInstance = new bootstrap.Modal(document.getElementById('addWidgetModal'))
    // eslint-disable-next-line no-undef
    this.textWidgetModalInstance = new bootstrap.Modal(document.getElementById('textWidgetModal'))
    // eslint-disable-next-line no-undef
    this.renameTabModalInstance = new bootstrap.Modal(document.getElementById('renameTabModal'))
    // eslint-disable-next-line no-undef
    this.addTabModalInstance = new bootstrap.Modal(document.getElementById('addTabModal'))
    
    // Fetch filter data
    this.fetchUsers()
    this.fetchWarehouses()
  },
  methods: {
    isWidgetAllowedForEnabledModules(widget) {
      if (!widget) return false

      const requiredModuleKeys = WIDGET_MODULE_KEYS[widget.module]
      // User-created text widgets and future uncategorized widgets do not
      // depend on a business module.
      if (!requiredModuleKeys) return true

      const modules = this.$store.getters.MODULES || []
      return requiredModuleKeys.every(moduleKey => (
        modules.some(module => module.module_key === moduleKey && Boolean(module.enabled))
      ))
    },
    isWidgetAllowedForPlan(widget) {
      if (!widget) return false
      if (!this.isStarterPlan) return true
      return this.starterInsightTitles.includes(widget.title)
    },
    isPresetAllowedForPlan(presetName) {
      return this.filterWidgetsForCurrentPlan(this.dashboardPresets[presetName] || []).length > 0
    },
    filterWidgetsForCurrentPlan(widgets = []) {
      const financialReplacements = {
        paid_invoiced_amount: 'income_sum',
        pending_invoiced_amount: 'expense_sum'
      }
      const financialWidgetTitles = new Set(Object.values(financialReplacements))
      const seenFinancialWidgets = new Set()

      return widgets
        .map(widget => {
          const replacementTitle = financialReplacements[widget.title]
          if (!replacementTitle) return widget

          const replacement = this.availableWidgets.find(candidate => candidate.title === replacementTitle)
          return replacement
            ? { ...replacement, id: widget.id, width: widget.width }
            : widget
        })
        .filter(widget => {
          const widgetKey = `${widget.module || 'other'}:${widget.title}`
          if (financialWidgetTitles.has(widget.title) && seenFinancialWidgets.has(widgetKey)) return false

          const isVisible = (
            !this.hiddenInsightTitles.includes(widget.title) &&
            this.isWidgetAllowedForEnabledModules(widget) &&
            this.isWidgetAllowedForPlan(widget)
          )
          if (isVisible && financialWidgetTitles.has(widget.title)) seenFinancialWidgets.add(widgetKey)
          return isVisible
        })
    },
    createRuntimeWidget(widget) {
      const defaultWidget = this.visibleAvailableWidgets.find(aw => aw.title === widget.title);
      const runtimeWidget = {
        ...(defaultWidget || {}),
        ...widget,
        value: 0,
        loading: true,
        tableData: [],
        chartOptions: {},
        chartData: { labels: [], datasets: [] },
        categoryBreakdown: [],
        barCardItems: [],
        chartJsType: 'bar'
      }
      runtimeWidget.width = this.widgetWidthClass(runtimeWidget)
      return runtimeWidget
    },
    firstTimeDefaultTabs() {
      const defaultTabs = [
        { id: 'home', nameKey: 'home', icon: 'bi-house-door', preset: 'home' },
        { id: 'sales', nameKey: 'sales', icon: 'bi-receipt', preset: 'sales' },
        { id: 'purchases', nameKey: 'purchases', icon: 'bi-cart3', preset: 'purchases' },
        { id: 'stock', nameKey: 'stock', icon: 'bi-box-seam', preset: 'inventory' },
        { id: 'finance', nameKey: 'finance', icon: 'bi-wallet2', preset: 'financial' }
      ]

      return defaultTabs.reduce((tabs, tabDefinition) => {
        const widgets = this.filterWidgetsForCurrentPlan(
          this.dashboardPresets[tabDefinition.preset] || []
        ).map((widget, index) => this.createRuntimeWidget({
          ...widget,
          id: `default_${tabDefinition.id}_${index}`
        }))

        tabs.push({
          id: `default_${tabDefinition.id}`,
          name: this.$t(tabDefinition.nameKey),
          nameKey: tabDefinition.nameKey,
          icon: tabDefinition.icon,
          widgets
        })

        return tabs
      }, [])
    },
    serializeWidgetForLayout(w) {
      return {
        title: w.title,
        type: w.type,
        endpoint: w.endpoint,
        dataKey: w.dataKey,
        dataPath: w.dataPath,
        icon: w.icon,
        description: w.description,
        chartType: w.chartType,
        transform: w.transform,
        id: w.id,
        width: this.widgetWidthClass(w),
        isAnalytics: w.isAnalytics,
        compare: w.compare,
        format: w.format,
        module: w.module,
        columns: w.columns ? JSON.parse(JSON.stringify(w.columns)) : w.columns,
        textTitle: w.textTitle,
        textContent: w.textContent,
        productName: w.productName
      }
    },
    getActiveTabLayoutSnapshot() {
      return this.activeTabWidgets.map(w => this.serializeWidgetForLayout(w))
    },
    widgetUsesDateRange(widget) {
      if (widget.type === 'payment_sales_summary') return false

      const allTimeTitles = [
        'total_products',
        'out_of_stock',
        'total_clients',
        'total_suppliers',
        'total_customers_balance',
        'pending_invoices',
        'overdue_invoices',
        'pending_invoiced_amount',
        'stock_value',
        'potential_selling_value',
        'potential_profit_in_stock',
        'low_stock_products',
        'out_of_stock_products',
        'in_stock_products',
        'products_close_to_minimum_stock',
        'products_without_purchase_price',
        'products_without_sale_price',
        'expiring_products',
        'expired_products_in_stock',
        'low_stock_product_list',
        'products_close_to_minimum_stock_list',
        'out_of_stock_product_list',
        'products_without_purchase_price_list',
        'products_without_sale_price_list',
        'expiring_product_list',
        'expired_products_in_stock_list',
        'stock_by_category',
        'stock_by_warehouse',
        'supplier_payment_status'
      ]
      return !allTimeTitles.includes(widget.title)
    },
    widgetDateLabel(widget) {
      if (!this.widgetUsesDateRange(widget)) return this.$t('all_time')
      return this.start && this.end ? `${this.start} ➝ ${this.end}` : this.$t('all_time')
    },
    isDateAxisChart(widget) {
      return Boolean(widget && widget.type === 'chart' && DATE_AXIS_CHART_TYPES.has(widget.chartType))
    },
    isFinancialCategoryBreakdown(widget) {
      return Boolean(widget && widget.type === 'chart' && FINANCIAL_CATEGORY_CHART_TYPES.has(widget.chartType))
    },
    getFinancialCategoryName(item, widget) {
      if (item && item.category_name) return item.category_name

      const sourceLabels = {
        invoice: 'invoice_payments',
        receipt: 'receipt_payments',
        pos: 'pos_payments',
        supplier_invoice: 'supplier_invoice_payments',
        purchase: 'purchase_payments',
        subscription: 'subscription_payments',
        project: 'project_payments',
        supplier: 'supplier_payments',
        client: 'client_payments',
        other_expense: 'other_expenses',
        other_income: 'other_income'
      }
      const labelKey = item && sourceLabels[item.source_type]
      const fallbackKey = widget && widget.chartType === 'financial_expense_by_category'
        ? 'other_expenses'
        : 'other_income'
      return this.$t(labelKey || fallbackKey)
    },
    isBarChart(widget) {
      return Boolean(
        widget &&
        widget.type === 'chart' &&
        !this.isFinancialCategoryBreakdown(widget) &&
        BAR_CHART_JS_TYPES.has(widget.chartJsType)
      )
    },
    buildBarCardItems(widget) {
      const chartData = widget.chartData || {}
      const labels = Array.isArray(chartData.labels) ? chartData.labels : []
      const dataset = Array.isArray(chartData.datasets) ? chartData.datasets[0] : null
      const values = dataset && Array.isArray(dataset.data) ? dataset.data : []
      const numericValues = values.map(value => {
        const numericValue = Number(value)
        return Number.isFinite(numericValue) ? numericValue : 0
      })
      const maximum = numericValues.reduce((currentMax, value) => (
        Math.max(currentMax, Math.abs(value))
      ), 0)
      const colors = dataset ? dataset.backgroundColor : null

      const items = labels.map((label, index) => {
        const value = numericValues[index] || 0
        const color = Array.isArray(colors)
          ? (colors[index % colors.length] || '#0d6efd')
          : (typeof colors === 'string' ? colors : '#0d6efd')

        return {
          label: label || this.$t('unknown'),
          value,
          color,
          percentage: maximum > 0
            ? Math.max((Math.abs(value) / maximum) * 100, value === 0 ? 0 : 2)
            : 0
        }
      })

      // Keep time-series cards chronological; rank all other bar data by value,
      // matching the Expense by Category card behavior.
      return this.isDateAxisChart(widget)
        ? items
        : items.sort((first, second) => Math.abs(second.value) - Math.abs(first.value))
    },
    formatBarCardValue(widget, value) {
      if (CURRENCY_CHART_TYPES.has(widget.chartType)) {
        return this.formatChartCurrency(value)
      }
      if (PERCENTAGE_CHART_TYPES.has(widget.chartType)) {
        return `${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`
      }
      return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })
    },
    widgetWidthClass(widget) {
      if (this.isDateAxisChart(widget)) return FULL_WIDTH_CLASS
      return widget.width || 'col-md-4 col-lg-3'
    },
    showAddWidgetModal() {
      this.widgetSearchQuery = ''
      this.selectedWidgetModule = 'all'
      this.modalInstance.show()
    },
    startEditing() {
      this.isEditing = true
      this.hasUnsavedChanges = false
      this.originalWidgets = this.getActiveTabLayoutSnapshot()
    },
    cancelEdit() {
      if (this.hasUnsavedChanges && Array.isArray(this.originalWidgets)) {
        this.activeTabWidgets = this.originalWidgets.map(widget => this.createRuntimeWidget(widget))
        this.refreshWidgets()
      }
      this.isEditing = false
      this.hasUnsavedChanges = false
      this.originalWidgets = null
    },
    addWidget(option) {
      if (!this.isWidgetAllowedForEnabledModules(option) || !this.isWidgetAllowedForPlan(option)) return

      // Capture the saved layout before adding anything. Previously edit mode was
      // enabled after the push, leaving originalWidgets as the initial empty
      // array; Cancel would then remove every widget from the dashboard.
      this.ensureEditingSnapshot()
      const newWidget = {
        ...option,
        id: Date.now() + Math.random().toString(36).substr(2, 9),
        value: 0,
        loading: true,
        tableData: [],
        chartOptions: {},
        chartData: { labels: [], datasets: [] },
        categoryBreakdown: [],
        barCardItems: [],
        chartJsType: 'bar',
        width: this.isDateAxisChart(option)
          ? FULL_WIDTH_CLASS
          : option.type === 'table'
          ? 'col-md-12 col-lg-12'
          : (['chart', 'sales_revenue_breakdown', 'payment_sales_summary'].includes(option.type) ? 'col-md-6 col-lg-6' : 'col-md-4 col-lg-3')
      }
      this.widgets.push(newWidget)
      this.fetchWidgetData(newWidget)
      this.modalInstance.hide()
      this.hasUnsavedChanges = true
    },
    showTextWidgetModal() {
      this.textWidgetConfig = {
        title: '',
        content: ''
      }
      this.modalInstance.hide()
      this.textWidgetModalInstance.show()
    },
    addTextWidget() {
      if (!this.textWidgetConfig.title) {
        if (this.$toast) {
          this.$toast.open({
            message: this.$t('title_required'),
            type: 'error',
          })
        }
        return
      }
      
      const newWidget = {
        id: Date.now() + Math.random().toString(36).substr(2, 9),
        type: 'text',
        title: 'text_widget',
        textTitle: this.textWidgetConfig.title,
        textContent: this.textWidgetConfig.content,
        icon: 'bi-fonts',
        width: 'col-md-12 col-lg-12',
        loading: false,
        module: 'other'
      }
      
      this.ensureEditingSnapshot()
      this.widgets.push(newWidget)
      this.textWidgetModalInstance.hide()
      this.hasUnsavedChanges = true
      
      if (this.$toast) {
        this.$toast.open({
          message: this.$t('widget_added_successfully'),
          type: 'success',
          hasUnsavedChanges: true
        })
      }
    },
    ensureEditingSnapshot() {
      if (!this.isEditing) {
        this.originalWidgets = this.getActiveTabLayoutSnapshot()
        this.isEditing = true
      }
    },
    removeWidget(index) {
      this.ensureEditingSnapshot()
      this.widgets.splice(index, 1)
      this.hasUnsavedChanges = true
    },
    resizeWidget(index, width) {
      this.ensureEditingSnapshot()
      const widget = this.widgets[index]
      this.$set(widget, 'width', this.isDateAxisChart(widget) ? FULL_WIDTH_CLASS : width)
      this.hasUnsavedChanges = true
    },
    // Dashboard preset loading
    async loadPreset(presetName) {
      const preset = this.filterWidgetsForCurrentPlan(this.dashboardPresets[presetName] || [])
      if (!preset.length) return
      
      // Confirm if widgets exist
      if (this.widgets.length > 0) {
        const result = await this.$swal.fire({
          title: this.$t('load_preset'),
          text: this.$t('load_preset_confirm'),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#0d6efd',
          cancelButtonColor: '#6c757d',
          confirmButtonText: this.$t('yes'),
          cancelButtonText: this.$t('cancel')
        })
        if (!result.isConfirmed) return
      }
      
      // Load preset widgets
      this.widgets = preset.map(w => this.createRuntimeWidget({
        ...w,
        id: Date.now() + Math.random().toString(36).substr(2, 9),
      }))
      
      this.refreshWidgets()
      await this.saveLayout({ silent: true })
      
      if (this.$toast) {
        this.$toast.open({
          message: this.$t('preset_loaded_successfully'),
          type: 'success',
        })
      }
    },
    // Clear dashboard
    async clearDashboard() {
      if (this.widgets.length === 0) return
      
      const result = await this.$swal.fire({
        title: this.$t('clear_dashboard'),
        text: this.$t('clear_dashboard_confirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: this.$t('yes'),
        cancelButtonText: this.$t('cancel')
      })
      
      if (result.isConfirmed) {
        this.ensureEditingSnapshot()
        this.widgets = []
        this.hasUnsavedChanges = true
        if (this.$toast) {
          this.$toast.open({
            message: this.$t('dashboard_cleared'),
            type: 'success',
          })
        }
      }
    },
    // Duplicate widget
    duplicateWidget(index) {
      this.ensureEditingSnapshot()
      const widget = this.widgets[index]
      const duplicate = {
        ...widget,
        id: Date.now() + Math.random().toString(36).substr(2, 9),
        value: widget.value,
        loading: false
      }
      this.widgets.splice(index + 1, 0, duplicate)
      this.hasUnsavedChanges = true
      
      if (this.$toast) {
        this.$toast.open({
          message: this.$t('widget_duplicated'),
          type: 'success',
        })
      }
    },
    // Move widget to position
    moveWidget(fromIndex, toIndex) {
      if (fromIndex === toIndex) return
      this.ensureEditingSnapshot()
      const widget = this.widgets.splice(fromIndex, 1)[0]
      this.widgets.splice(toIndex, 0, widget)
      this.hasUnsavedChanges = true
    },
    switchTab(tabId) {
      this.activeTabId = tabId
      this.refreshWidgets()
    },
    addNewTab() {
      this.newTabConfig = {
        name: '',
        icon: 'bi-grid-1x2',
        preset: ''
      }
      this.addTabModalInstance.show()
    },
    async confirmAddTab() {
      if (!this.newTabConfig.name.trim()) {
        if (this.$toast) {
          this.$toast.open({
            message: this.$t('name_required') || 'Name is required',
            type: 'error'
          })
        }
        return
      }

      const newTabId = 'tab_' + Date.now() + Math.random().toString(36).substr(2, 5)
      let initialWidgets = []

      if (this.newTabConfig.preset) {
        const preset = this.filterWidgetsForCurrentPlan(this.dashboardPresets[this.newTabConfig.preset] || [])
        if (preset.length) {
          initialWidgets = preset.map(w => this.createRuntimeWidget({
            ...w,
            id: Date.now() + Math.random().toString(36).substr(2, 9),
          }))
        }
      }

      const newTab = {
        id: newTabId,
        name: this.newTabConfig.name.trim(),
        icon: this.newTabConfig.icon,
        widgets: initialWidgets
      }

      this.tabs.push(newTab)
      this.activeTabId = newTabId
      this.addTabModalInstance.hide()
      this.isEditing = false

      this.refreshWidgets()
      await this.saveLayout()
    },
    showRenameTabModal(tab) {
      this.renameTabConfig = {
        id: tab.id,
        name: tab.nameKey ? this.$t(tab.nameKey) : tab.name,
        icon: tab.icon || 'bi-grid-1x2'
      }
      this.renameTabModalInstance.show()
    },
    async confirmRenameTab() {
      if (!this.renameTabConfig.name.trim()) {
        if (this.$toast) {
          this.$toast.open({
            message: this.$t('name_required') || 'Name is required',
            type: 'error'
          })
        }
        return
      }

      const tab = this.tabs.find(t => t.id === this.renameTabConfig.id)
      if (tab) {
        tab.name = this.renameTabConfig.name.trim()
        this.$delete(tab, 'nameKey')
        tab.icon = this.renameTabConfig.icon
      }

      this.renameTabModalInstance.hide()
      this.isEditing = false
      await this.saveLayout()
    },
    async deleteTab(tabId) {
      if (this.tabs.length <= 1) return

      const result = await this.$swal.fire({
        title: this.$t('delete') || 'Delete',
        text: this.$t('are_you_sure_delete_tab') || 'Are you sure you want to delete this dashboard tab?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: this.$t('yes') || 'Yes',
        cancelButtonText: this.$t('cancel') || 'Cancel'
      })

      if (result.isConfirmed) {
        const index = this.tabs.findIndex(t => t.id === tabId)
        if (index !== -1) {
          this.tabs.splice(index, 1)
          // If we deleted the active tab, switch to the first available tab
          if (this.activeTabId === tabId) {
            this.activeTabId = this.tabs[0].id
          }
          this.isEditing = false
          this.refreshWidgets()
          await this.saveLayout()
        }
      }
    },
    refreshAllTabs() {
      this.tabs.forEach(tab => {
        if (tab.widgets) {
          tab.widgets.forEach(w => {
            if (w.type !== 'text') {
              w.loading = true
              this.fetchWidgetData(w)
            }
          })
        }
      })
    },
    async saveLayout(options = {}) {
      // Save all tabs layout structure (only necessary serializable properties of widgets)
      const tabsData = this.tabs.map(tab => ({
        id: tab.id,
        name: tab.name,
        nameKey: tab.nameKey,
        icon: tab.icon,
        widgets: (tab.widgets || []).map(w => this.serializeWidgetForLayout(w))
      }))

      const templateData = {
        tabs: tabsData,
        activeTabId: this.activeTabId
      }
      
      try {
          await helper.customPost(`${process.env.VUE_APP_API}/templates`, {
            name: 'Advanced Insights Layout',
            type: 'advanced_insights',
            data: templateData
          })
          
          this.isEditing = false
          this.hasUnsavedChanges = false
          this.originalWidgets = null
          
          if (!options.silent && this.$toast) {
              this.$toast.open({
                  message: this.$t('successfully_saved'),
                  type: 'success',
              });
          }
      } catch (e) {
          if (!options.silent) alert(e)
          if (!options.silent && this.$toast) {
              this.$toast.open({
                  message: this.$t('error_occurred'),
                  type: 'error',
              });
          }
      }
    },
    async fetchWidgetData(widget) {
      if (!this.isWidgetAllowedForEnabledModules(widget)) {
        widget.loading = false
        return
      }
      if (widget.type === 'product_search_profitability') {
        widget.loading = false
        return
      }
      try {
        // Build query parameters for filters
        const filterParams = new URLSearchParams()
        
        if (this.start && this.end && this.widgetUsesDateRange(widget)) {
          filterParams.append('start', this.start)
          filterParams.append('end', this.end)
        }
        
        if (this.selectedUserId) {
          filterParams.append('user_id', this.selectedUserId)
        }
        
        if (this.selectedWarehouseId) {
          filterParams.append('warehouse_id', this.selectedWarehouseId)
        }

        if (widget.type === 'payment_sales_summary') {
          filterParams.append('anchor_date', moment().format('YYYY-MM-DD'))
        }
        
        const filterString = filterParams.toString()
        
         // Handle analytics endpoints
         if (widget.endpoint.includes('/v2/analytics')) {
              const separator = widget.endpoint.includes('?') ? '&' : '?'
              const url = `${process.env.VUE_APP_API}${widget.endpoint}${separator}${filterString}`
              const res = await helper.customGet(url)
              
              if (widget.type === 'chart') {
                  this.processChartData(widget, res.data.data || res.data)
              } else if (widget.type === 'table') {
                  this.$set(widget, 'tableData', res.data.data || res.data)
              } else if (widget.isAnalytics) {
                  // Handle analytics metrics
                  widget.value = res.data[widget.dataKey] !== undefined ? res.data[widget.dataKey] : (res.data.data !== undefined ? res.data.data : 0)
              }
         }  
        // Handle advanced insights endpoints
        else if (widget.endpoint.includes('/v2/insights/')) {
            let url = `${process.env.VUE_APP_API}${widget.endpoint}`
            if (filterString) {
              url += `?${filterString}`
            }
            const res = await helper.customGet(url)
            let payload = widget.dataPath ? this.getNestedValue(res.data, widget.dataPath) : res.data
            payload = this.transformWidgetPayload(widget, payload)

            if (widget.type === 'chart') {
                this.processChartData(widget, payload)
            } else if (widget.type === 'table') {
                this.$set(widget, 'tableData', payload || [])
            } else {
                widget.value = payload !== undefined ? payload : 0
            }
        }
        // Handle POS endpoints
        else if (widget.isPos) {
            let url = `${process.env.VUE_APP_API}${widget.endpoint}`
            if (filterString) {
              url += `?${filterString}`
            }
            const res = await helper.customGet(url)
            
            if (widget.type === 'chart') {
                this.processChartData(widget, res.data)
            } else {
                // Handle POS metrics
                widget.value = res.data[widget.dataKey] !== undefined ? res.data[widget.dataKey] : 0
            }
        }
        // Handle insights endpoints (with filters support)
        else if (widget.endpoint.includes('/insights/')) {
            let url = `${process.env.VUE_APP_API}${widget.endpoint}`
            if (filterString) {
              url += `?${filterString}`
            }
            const res = await helper.customGet(url)
            widget.value = res.data[widget.dataKey] !== undefined ? res.data[widget.dataKey] : 0
        }
        // Handle regular endpoints
        else {
            let url = `${process.env.VUE_APP_API}${widget.endpoint}?per_page=1`
            if (filterString) {
              url += `&${filterString}`
            }
            const res = await helper.customGet(url)
            widget.value = res.data[widget.dataKey] !== undefined ? res.data[widget.dataKey] : (res.data.total !== undefined ? res.data.total : 0)
        }

        // Fetch comparison (previous period) data for number widgets
        if (widget.type === 'number' && widget.compare !== false && this.start && this.end && !['total_products', 'out_of_stock'].includes(widget.title)) {
          const currentStart = moment(this.start, 'YYYY-MM-DD')
          const currentEnd = moment(this.end, 'YYYY-MM-DD')
          const diffDays = currentEnd.diff(currentStart, 'days') + 1
          const prevStart = moment(this.start, 'YYYY-MM-DD').subtract(diffDays, 'days').format('YYYY-MM-DD')
          const prevEnd = moment(this.start, 'YYYY-MM-DD').subtract(1, 'days').format('YYYY-MM-DD')

          const prevParams = new URLSearchParams()
          prevParams.append('start', prevStart)
          prevParams.append('end', prevEnd)
          if (this.selectedUserId) prevParams.append('user_id', this.selectedUserId)
          if (this.selectedWarehouseId) prevParams.append('warehouse_id', this.selectedWarehouseId)

          const prevFilterString = prevParams.toString()
          let prevUrl = ''

          if (widget.endpoint.includes('/v2/analytics')) {
            const separator = widget.endpoint.includes('?') ? '&' : '?'
            prevUrl = `${process.env.VUE_APP_API}${widget.endpoint}${separator}${prevFilterString}`
          } else if (widget.isPos || widget.endpoint.includes('/insights/')) {
            prevUrl = `${process.env.VUE_APP_API}${widget.endpoint}?${prevFilterString}`
          } else {
            prevUrl = `${process.env.VUE_APP_API}${widget.endpoint}?per_page=1&${prevFilterString}`
          }

          try {
            const prevRes = await helper.customGet(prevUrl)
            let prevValue = 0
            if (widget.endpoint.includes('/v2/analytics')) {
              prevValue = prevRes.data[widget.dataKey] !== undefined ? prevRes.data[widget.dataKey] : (prevRes.data.data !== undefined ? prevRes.data.data : 0)
            } else if (widget.endpoint.includes('/v2/insights/')) {
              prevValue = widget.dataPath ? this.getNestedValue(prevRes.data, widget.dataPath) : 0
            } else if (widget.isPos || widget.endpoint.includes('/insights/')) {
              prevValue = prevRes.data[widget.dataKey] !== undefined ? prevRes.data[widget.dataKey] : 0
            } else {
              prevValue = prevRes.data[widget.dataKey] !== undefined ? prevRes.data[widget.dataKey] : (prevRes.data.total !== undefined ? prevRes.data.total : 0)
            }

            const currValFloat = parseFloat(widget.value) || 0
            const prevValFloat = parseFloat(prevValue) || 0

            this.$set(widget, 'previousValue', prevValue)

            if (prevValFloat > 0) {
              const trend = ((currValFloat - prevValFloat) / prevValFloat) * 100
              this.$set(widget, 'trend', parseFloat(trend.toFixed(1)))
            } else if (currValFloat > 0) {
              this.$set(widget, 'trend', 100)
            } else {
              this.$set(widget, 'trend', 0)
            }
          } catch (err) {
            alert('Error fetching comparison value:', err)
            this.$set(widget, 'previousValue', 0)
            this.$set(widget, 'trend', 0)
          }
        } else {
          // If no date range filters applied (e.g. all-time), reset comparison values
          this.$set(widget, 'previousValue', undefined)
          this.$set(widget, 'trend', undefined)
        }
        
        widget.loading = false
      } catch (e) {
        alert(e)
        widget.loading = false
        widget.value = '-'
      }
    },
    formatChartCurrency(value) {
      const amount = Number(value)
      return helper.formatMoney(Number.isFinite(amount) ? amount : 0)
    },
    applyCurrencyChartFormatting(widget) {
      const chartOptions = widget.chartOptions || {}
      const tooltips = chartOptions.tooltips || {}
      const tooltipCallbacks = tooltips.callbacks || {}

      const formattedOptions = {
        ...chartOptions,
        tooltips: {
          ...tooltips,
          callbacks: {
            ...tooltipCallbacks,
            label: (tooltipItem, chartData) => {
              const dataset = chartData.datasets[tooltipItem.datasetIndex || 0] || {}
              const value = dataset.data ? dataset.data[tooltipItem.index] : 0
              const label = ['pie', 'doughnut'].includes(widget.chartJsType)
                ? (chartData.labels[tooltipItem.index] || '')
                : (dataset.label || '')
              const formattedValue = this.formatChartCurrency(value)
              return label ? `${label}: ${formattedValue}` : formattedValue
            }
          }
        }
      }

      if (!['pie', 'doughnut'].includes(widget.chartJsType)) {
        const axisKey = widget.chartJsType === 'horizontalBar' ? 'xAxes' : 'yAxes'
        const scales = chartOptions.scales || {}
        const axes = scales[axisKey] && scales[axisKey].length
          ? scales[axisKey]
          : [{}]

        formattedOptions.scales = {
          ...scales,
          [axisKey]: axes.map((axis, index) => index === 0
            ? {
                ...axis,
                ticks: {
                  ...(axis.ticks || {}),
                  callback: value => this.formatChartCurrency(value)
                }
              }
            : axis)
        }
      }

      widget.chartOptions = formattedOptions
    },
    processChartData(widget, data) {
        // Validate data is an array
        if (!data || !Array.isArray(data)) {
          if (this.isFinancialCategoryBreakdown(widget)) {
            this.$set(widget, 'categoryBreakdown', [])
          }
          this.$set(widget, 'barCardItems', [])
          widget.chartData = { labels: [], datasets: [] }
          widget.loading = false
          return
        }
        
        if (widget.chartType === 'revenue_trend') {
            const categories = data.map(d => d.order_date)
            const values = data.map(d => parseFloat(d.daily_revenue).toFixed(2))
            widget.chartJsType = 'bar'
            widget.chartData = {
                labels: categories,
                datasets: [{
                    label: this.$t('revenue'),
                    data: values,
                    backgroundColor: '#0d6efd',
                    borderColor: '#0d6efd',
                    borderWidth: 1
                }]
            }
            widget.chartOptions = {
                legend: { display: false },
                scales: {
                    yAxes: [{ ticks: { beginAtZero: true } }]
                }
            }
        } else if (widget.chartType === 'advanced_sales_trend') {
            const categories = data.map(d => d.period)
            const values = data.map(d => parseFloat(d.total_sales || 0).toFixed(2))
            widget.chartJsType = 'line'
            widget.chartData = {
                labels: categories,
                datasets: [{
                    label: this.$t('sales'),
                    data: values,
                    borderColor: '#198754',
                    backgroundColor: 'rgba(25, 135, 84, 0.12)',
                    fill: true,
                    tension: 0.4
                }]
            }
            widget.chartOptions = {
                legend: { display: false },
                scales: {
                    yAxes: [{ ticks: { beginAtZero: true } }]
                }
            }
        } else if (widget.chartType === 'advanced_best_selling_products' || widget.chartType === 'advanced_worst_selling_products') {
             const categories = data.map(d => d.product_name || d.label || '-')
             const values = data.map(d => parseFloat(d.total_quantity || d.total_sold || 0))
             const isWorst = widget.chartType === 'advanced_worst_selling_products'
             widget.chartJsType = 'horizontalBar'
             widget.chartData = {
                labels: categories,
                datasets: [{
                    label: this.$t('quantity'),
                    data: values,
                    backgroundColor: isWorst ? '#dc3545' : '#ffc107',
                    borderColor: isWorst ? '#dc3545' : '#ffc107',
                    borderWidth: 1
                }]
            }
            widget.chartOptions = {
                legend: { display: false },
                scales: {
                    xAxes: [{ ticks: { beginAtZero: true } }]
                }
            }
        } else if (widget.chartType === 'advanced_fast_moving_products' || widget.chartType === 'advanced_slow_moving_products') {
             const categories = data.map(d => d.product_name || d.label || '-')
             const values = data.map(d => parseFloat(d.total_sold || d.total_quantity || 0))
             const isSlow = widget.chartType === 'advanced_slow_moving_products'
             widget.chartJsType = 'horizontalBar'
             widget.chartData = {
                labels: categories,
                datasets: [{
                    label: this.$t('quantity'),
                    data: values,
                    backgroundColor: isSlow ? '#dc3545' : '#20c997',
                    borderColor: isSlow ? '#dc3545' : '#20c997',
                    borderWidth: 1
                }]
            }
            widget.chartOptions = {
                legend: { display: false },
                scales: {
                    xAxes: [{ ticks: { beginAtZero: true } }]
                }
            }
        } else if (widget.chartType === 'advanced_sales_by_client') {
             const categories = data.map(d => d.client_name || this.$t('unknown') || '-')
             const values = data.map(d => parseFloat(d.total_sales || 0).toFixed(2))
             widget.chartJsType = 'horizontalBar'
             widget.chartData = {
                labels: categories,
                datasets: [{
                    label: this.$t('sales'),
                    data: values,
                    backgroundColor: '#0dcaf0',
                    borderColor: '#0dcaf0',
                    borderWidth: 1
                }]
            }
            widget.chartOptions = {
                legend: { display: false },
                scales: {
                    xAxes: [{ ticks: { beginAtZero: true } }]
                }
            }
        } else if (widget.chartType === 'advanced_clients_by_revenue') {
             const categories = data.map(d => d.client_name || this.$t('unknown') || '-')
             const values = data.map(d => parseFloat(d.revenue || 0).toFixed(2))
             widget.chartJsType = 'horizontalBar'
             widget.chartData = {
                labels: categories,
                datasets: [{
                    label: this.$t('revenue'),
                    data: values,
                    backgroundColor: '#0d6efd',
                    borderColor: '#0d6efd',
                    borderWidth: 1
                }]
             }
             widget.chartOptions = {
                legend: { display: false },
                scales: { xAxes: [{ ticks: { beginAtZero: true } }] }
             }
        } else if (widget.chartType === 'advanced_leads_by_status') {
             const labels = data.map(d => d.status || this.$t('unknown'))
             const values = data.map(d => Number(d.count) || 0)
             widget.chartJsType = 'doughnut'
             widget.chartData = {
                labels,
                datasets: [{
                    data: values,
                    backgroundColor: ['#0d6efd', '#198754', '#ffc107', '#dc3545', '#6f42c1', '#fd7e14', '#20c997', '#0dcaf0']
                }]
             }
             widget.chartOptions = { legend: { position: 'bottom' } }
        } else if (widget.chartType === 'advanced_leads_by_assigned_person') {
             const labels = data.map(d => d.assigned_person || (d.user_id ? this.$t('unknown') : this.$t('unassigned')))
             const values = data.map(d => Number(d.count) || 0)
             widget.chartJsType = 'horizontalBar'
             widget.chartData = {
                labels,
                datasets: [{
                    label: this.$t('leads'),
                    data: values,
                    backgroundColor: '#20c997',
                    borderColor: '#20c997',
                    borderWidth: 1
                }]
             }
             widget.chartOptions = {
                legend: { display: false },
                scales: { xAxes: [{ ticks: { beginAtZero: true, precision: 0 } }] }
             }
        } else if (widget.chartType === 'advanced_sales_by_payment_method') {
             const labels = data.map(d => d.payment_method || this.$t('unknown') || '-')
             const values = data.map(d => parseFloat(d.total_sales || 0).toFixed(2))
             widget.chartJsType = 'doughnut'
             widget.chartData = {
                labels: labels,
                datasets: [{
                    data: values,
                    backgroundColor: ['#198754', '#0d6efd', '#ffc107', '#dc3545', '#6f42c1', '#fd7e14', '#20c997']
                }]
            }
            widget.chartOptions = {
                legend: { position: 'bottom' }
            }
        } else if (widget.chartType === 'advanced_profit_by_product') {
             const categories = data.map(d => d.product_name || '-')
             const values = data.map(d => parseFloat(d.margin_percentage || 0).toFixed(2))
             widget.chartJsType = 'horizontalBar'
             widget.chartData = {
                labels: categories,
                datasets: [{
                    label: this.$t('profit_margin'),
                    data: values,
                    backgroundColor: '#198754',
                    borderColor: '#198754',
                    borderWidth: 1
                }]
            }
            widget.chartOptions = {
                legend: { display: false },
                scales: {
                    xAxes: [{ ticks: { beginAtZero: true } }]
                }
            }
        } else if (widget.chartType === 'advanced_profit_by_category') {
             const categories = data.map(d => d.category_name || this.$t('uncategorized') || '-')
             const values = data.map(d => parseFloat(d.margin_percentage || 0).toFixed(2))
             widget.chartJsType = 'bar'
             widget.chartData = {
                labels: categories,
                datasets: [{
                    label: this.$t('profit_margin'),
                    data: values,
                    backgroundColor: '#6f42c1',
                    borderColor: '#6f42c1',
                    borderWidth: 1
                }]
            }
            widget.chartOptions = {
                legend: { display: false },
                scales: {
                    yAxes: [{ ticks: { beginAtZero: true } }]
                }
            }
        } else if (widget.chartType === 'advanced_purchases_by_supplier') {
             const categories = data.map(d => d.supplier_name || this.$t('unknown') || '-')
             const values = data.map(d => parseFloat(d.total_purchases || 0).toFixed(2))
             widget.chartJsType = 'horizontalBar'
             widget.chartData = {
                labels: categories,
                datasets: [{
                    label: this.$t('purchases'),
                    data: values,
                    backgroundColor: '#6f42c1',
                    borderColor: '#6f42c1',
                    borderWidth: 1
                }]
            }
            widget.chartOptions = {
                legend: { display: false },
                scales: {
                    xAxes: [{ ticks: { beginAtZero: true } }]
                }
            }
        } else if (widget.chartType === 'advanced_supplier_payment_status') {
             const labels = data.map(d => this.$t(d.status || 'unknown'))
             const values = data.map(d => parseFloat(d.outstanding_amount || d.total_amount || 0).toFixed(2))
             widget.chartJsType = 'doughnut'
             widget.chartData = {
                labels: labels,
                datasets: [{
                    data: values,
                    backgroundColor: ['#198754', '#ffc107', '#dc3545', '#0dcaf0', '#6f42c1', '#fd7e14']
                }]
            }
            widget.chartOptions = {
                legend: { position: 'bottom' }
            }
        } else if (widget.chartType === 'advanced_most_purchased_products') {
             const categories = data.map(d => d.product_name || '-')
             const values = data.map(d => parseFloat(d.total_quantity || 0))
             widget.chartJsType = 'horizontalBar'
             widget.chartData = {
                labels: categories,
                datasets: [{
                    label: this.$t('quantity'),
                    data: values,
                    backgroundColor: '#fd7e14',
                    borderColor: '#fd7e14',
                    borderWidth: 1
                }]
            }
            widget.chartOptions = {
                legend: { display: false },
                scales: {
                    xAxes: [{ ticks: { beginAtZero: true } }]
                }
            }
        } else if (widget.chartType === 'advanced_pos_sales_by_cashier') {
             const categories = data.map(d => d.cashier_name || this.$t('unknown') || '-')
             const values = data.map(d => parseFloat(d.total_sales || 0).toFixed(2))
             widget.chartJsType = 'horizontalBar'
             widget.chartData = {
                labels: categories,
                datasets: [{
                    label: this.$t('sales'),
                    data: values,
                    backgroundColor: '#0dcaf0',
                    borderColor: '#0dcaf0',
                    borderWidth: 1
                }]
            }
            widget.chartOptions = {
                legend: { display: false },
                scales: {
                    xAxes: [{ ticks: { beginAtZero: true } }]
                }
            }
        } else if (widget.chartType === 'advanced_pos_sales_by_register') {
             const categories = data.map(d => d.cash_register_reference || this.$t('unknown') || '-')
             const values = data.map(d => parseFloat(d.total_sales || 0).toFixed(2))
             widget.chartJsType = 'bar'
             widget.chartData = {
                labels: categories,
                datasets: [{
                    label: this.$t('sales'),
                    data: values,
                    backgroundColor: '#6f42c1',
                    borderColor: '#6f42c1',
                    borderWidth: 1
                }]
            }
            widget.chartOptions = {
                legend: { display: false },
                scales: {
                    yAxes: [{ ticks: { beginAtZero: true } }]
                }
            }
        } else if (widget.chartType === 'advanced_pos_active_hours') {
             const categories = data.map(d => `${d.hour}:00`)
             const values = data.map(d => parseFloat(d.transaction_count || 0))
             widget.chartJsType = 'bar'
             widget.chartData = {
                labels: categories,
                datasets: [{
                    label: this.$t('transactions'),
                    data: values,
                    backgroundColor: '#198754',
                    borderColor: '#198754',
                    borderWidth: 1
                }]
            }
            widget.chartOptions = {
                legend: { display: false },
                scales: {
                    yAxes: [{ ticks: { beginAtZero: true } }]
                }
            }
        } else if (widget.chartType === 'advanced_pos_daily_sales_trend') {
             const categories = data.map(d => d.period)
             const values = data.map(d => parseFloat(d.total_sales || 0).toFixed(2))
             widget.chartJsType = 'line'
             widget.chartData = {
                labels: categories,
                datasets: [{
                    label: this.$t('sales'),
                    data: values,
                    borderColor: '#198754',
                    backgroundColor: 'rgba(25, 135, 84, 0.12)',
                    fill: true,
                    tension: 0.4
                }]
            }
            widget.chartOptions = {
                legend: { display: false },
                scales: {
                    yAxes: [{ ticks: { beginAtZero: true } }]
                }
            }
        } else if (widget.chartType === 'advanced_pos_sales_by_product') {
             const categories = data.map(d => d.product_name || this.$t('unknown') || '-')
             const values = data.map(d => parseFloat(d.total_sales || 0).toFixed(2))
             widget.chartJsType = 'horizontalBar'
             widget.chartData = {
                labels: categories,
                datasets: [{
                    label: this.$t('sales'),
                    data: values,
                    backgroundColor: '#fd7e14',
                    borderColor: '#fd7e14',
                    borderWidth: 1
                }]
            }
            widget.chartOptions = {
                legend: { display: false },
                scales: {
                    xAxes: [{ ticks: { beginAtZero: true } }]
                }
            }
        } else if (widget.chartType === 'advanced_pos_orders_by_payment_method') {
             const labels = data.map(d => d.payment_method || this.$t('unknown') || '-')
             const values = data.map(d => parseFloat(d.order_count || d.count || 0))
             widget.chartJsType = 'doughnut'
             widget.chartData = {
                labels,
                datasets: [{
                    data: values,
                    backgroundColor: ['#198754', '#0d6efd', '#ffc107', '#dc3545', '#6f42c1', '#fd7e14', '#20c997']
                }]
            }
            widget.chartOptions = {
                legend: { position: 'bottom' }
            }
        } else if (widget.chartType === 'monthly_revenue') {
             const categories = data.map(d => `${d.month}/${d.year}`)
             const values = data.map(d => parseFloat(d.monthly_revenue).toFixed(2))
             widget.chartJsType = 'bar'
             widget.chartData = {
                labels: categories,
                datasets: [{
                    label: this.$t('revenue'),
                    data: values,
                    backgroundColor: '#198754',
                    borderColor: '#198754',
                    borderWidth: 1
                }]
            }
            widget.chartOptions = {
                legend: { display: false },
                scales: {
                    yAxes: [{ ticks: { beginAtZero: true } }]
                }
            }
        } else if (widget.chartType === 'top_selling_products') {
             const categories = data.map(d => d.product_label)
             const values = data.map(d => d.total_sold)
             widget.chartJsType = 'horizontalBar'
             widget.chartData = {
                labels: categories,
                datasets: [{
                    label: this.$t('sold'),
                    data: values,
                    backgroundColor: '#ffc107',
                    borderColor: '#ffc107',
                    borderWidth: 1
                }]
            }
            widget.chartOptions = {
                legend: { display: false },
                scales: {
                    xAxes: [{ ticks: { beginAtZero: true } }]
                }
            }
        } else if (widget.chartType === 'top_customers') {
             const categories = data.map(d => d.name)
             const values = data.map(d => parseFloat(d.total_revenue).toFixed(2))
             widget.chartJsType = 'horizontalBar'
             widget.chartData = {
                labels: categories,
                datasets: [{
                    label: this.$t('revenue'),
                    data: values,
                    backgroundColor: '#0dcaf0',
                    borderColor: '#0dcaf0',
                    borderWidth: 1
                }]
            }
            widget.chartOptions = {
                legend: { display: false },
                scales: {
                    xAxes: [{ ticks: { beginAtZero: true } }]
                }
            }
        } 
        // POS Chart Types
        else if (widget.chartType === 'pos_top_products') {
             const categories = data.map(d => d.product_label || d.label)
             const values = data.map(d => d.total_quantity || d.quantity)
             widget.chartJsType = 'horizontalBar'
             widget.chartData = {
                labels: categories,
                datasets: [{
                    label: this.$t('quantity'),
                    data: values,
                    backgroundColor: '#ff6384',
                    borderColor: '#ff6384',
                    borderWidth: 1
                }]
            }
            widget.chartOptions = {
                legend: { display: false },
                scales: {
                    xAxes: [{ ticks: { beginAtZero: true } }]
                }
            }
        } else if (widget.chartType === 'pos_payment_methods') {
             const labels = data.map(d => d.payment_method || d.method)
             const values = data.map(d => parseFloat(d.total || d.amount || 0).toFixed(2))
             widget.chartJsType = 'pie'
             widget.chartData = {
                labels: labels,
                datasets: [{
                    data: values,
                    backgroundColor: ['#36a2eb', '#ff6384', '#ffce56', '#4bc0c0', '#9966ff']
                }]
            }
            widget.chartOptions = {
                legend: { position: 'bottom' }
            }
        } else if (widget.chartType === 'pos_daily_sales') {
             const categories = data.map(d => d.date || d.day)
             const values = data.map(d => parseFloat(d.total_sales || d.sales || 0).toFixed(2))
             widget.chartJsType = 'line'
             widget.chartData = {
                labels: categories,
                datasets: [{
                    label: this.$t('sales'),
                    data: values,
                    borderColor: '#28a745',
                    backgroundColor: 'rgba(40, 167, 69, 0.2)',
                    fill: true,
                    tension: 0.4
                }]
            }
            widget.chartOptions = {
                legend: { display: false },
                scales: {
                    yAxes: [{ ticks: { beginAtZero: true } }]
                }
            }
        } else if (widget.chartType === 'stock_movement_summary') {
             const labels = data.map(d => this.$t(d.movement_type || 'unknown'))
             const values = data.map(d => parseFloat(d.quantity || 0))
             widget.chartJsType = 'bar'
             widget.chartData = {
                labels,
                datasets: [{
                    label: this.$t('quantity'),
                    data: values,
                    backgroundColor: ['#198754', '#0d6efd', '#20c997', '#dc3545', '#fd7e14'],
                    borderWidth: 1
                }]
            }
            widget.chartOptions = {
                legend: { display: false },
                scales: {
                    yAxes: [{ ticks: { beginAtZero: true } }]
                }
            }
        } else if (widget.chartType === 'inventory_stock_by_category') {
             const labels = data.map(d => d.category_name || this.$t('uncategorized') || '-')
             const values = data.map(d => parseFloat(d.stock_value || 0).toFixed(2))
             widget.chartJsType = 'doughnut'
             widget.chartData = {
                labels,
                datasets: [{
                    data: values,
                    backgroundColor: ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b', '#858796', '#5a5c69', '#fd7e14']
                }]
            }
            widget.chartOptions = {
                legend: { position: 'bottom' }
            }
        } else if (widget.chartType === 'inventory_stock_by_warehouse') {
             const categories = data.map(d => d.warehouse_name || this.$t('unknown') || '-')
             const values = data.map(d => parseFloat(d.stock_value || 0).toFixed(2))
             widget.chartJsType = 'horizontalBar'
             widget.chartData = {
                labels: categories,
                datasets: [{
                    label: this.$t('stock_value'),
                    data: values,
                    backgroundColor: '#0dcaf0',
                    borderColor: '#0dcaf0',
                    borderWidth: 1
                }]
            }
            widget.chartOptions = {
                legend: { display: false },
                scales: {
                    xAxes: [{ ticks: { beginAtZero: true } }]
                }
            }
        } else if (widget.chartType === 'sales_by_category' || widget.chartType === 'stock_by_category') {
             const labels = data.map(d => d.category_name || d.label)
             const values = data.map(d => parseFloat(d.total || d.value || 0).toFixed(2))
             widget.chartJsType = 'doughnut'
             widget.chartData = {
                labels: labels,
                datasets: [{
                    data: values,
                    backgroundColor: ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b', '#858796', '#5a5c69', '#f8f9fa'],
                    hoverBackgroundColor: ['#2e59d9', '#17a673', '#2c9faf', '#dda20a', '#be2617', '#60616f', '#373840', '#f8f9fa'],
                    hoverBorderColor: "rgba(234, 236, 244, 1)",
                }]
            }
            widget.chartOptions = {
                legend: { position: 'right' },
                cutoutPercentage: 80,
            }
        } else if (widget.chartType === 'invoices_status_trend') {
            const categories = data.map(d => d.date)
            const paid = data.map(d => parseFloat(d.paid_amount).toFixed(2))
            const pending = data.map(d => parseFloat(d.pending_amount).toFixed(2))
            const overdue = data.map(d => parseFloat(d.overdue_amount).toFixed(2))
            
            widget.chartJsType = 'line'
            widget.chartData = {
                labels: categories,
                datasets: [
                    {
                        label: this.$t('paid'),
                        data: paid,
                        borderColor: '#198754',
                        backgroundColor: 'rgba(25, 135, 84, 0.1)',
                        fill: false,
                        tension: 0.4
                    },
                    {
                        label: this.$t('pending'),
                        data: pending,
                        borderColor: '#ffc107',
                        backgroundColor: 'rgba(255, 193, 7, 0.1)',
                        fill: false,
                        tension: 0.4
                    },
                    {
                        label: this.$t('overdue'),
                        data: overdue,
                        borderColor: '#dc3545',
                        backgroundColor: 'rgba(220, 53, 69, 0.1)',
                        fill: false,
                        tension: 0.4
                    }
                ]
            }
            widget.chartOptions = {
                legend: { position: 'bottom' },
                scales: {
                    yAxes: [{ 
                      ticks: { beginAtZero: true },
                      gridLines: { borderDash: [2, 4] }
                    }],
                    xAxes: [{
                      gridLines: { display: false }
                    }]
                },
                tooltips: {
                  mode: 'index',
                  intersect: false
                }
            }
        } else if (widget.chartType === 'financial_income_by_category' || widget.chartType === 'financial_expense_by_category') {
            const sortedData = [...data].sort((first, second) => (
              Number(second.total_amount || 0) - Number(first.total_amount || 0)
            ))
            const total = sortedData.reduce((sum, item) => sum + Number(item.total_amount || 0), 0)
            this.$set(widget, 'categoryBreakdown', sortedData.map(item => {
              const amount = Number(item.total_amount || 0)
              return {
                ...item,
                total_amount: amount,
                percentage: total > 0 ? Math.max((amount / total) * 100, 2) : 0
              }
            }))
        } else if (widget.chartType === 'quotes_status_distribution') {
             const labels = data.map(d => this.$t(d.status))
             const values = data.map(d => d.count)
             widget.chartJsType = 'pie'
             widget.chartData = {
                labels: labels,
                datasets: [{
                    data: values,
                    backgroundColor: ['#198754', '#ffc107', '#0dcaf0', '#dc3545', '#6f42c1', '#fd7e14', '#20c997']
                }]
            }
            widget.chartOptions = {
                legend: { position: 'bottom' }
            }
        } else if (widget.chartType === 'invoices_status_distribution') {
             const labels = data.map(d => this.$t(d.status))
             const values = data.map(d => d.count)
             widget.chartJsType = 'pie'
             widget.chartData = {
                labels: labels,
                datasets: [{
                    data: values,
                    backgroundColor: ['#198754', '#ffc107', '#0dcaf0', '#dc3545', '#6f42c1', '#fd7e14', '#20c997']
                }]
            }
            widget.chartOptions = {
                legend: { position: 'bottom' }
            }
        } else if (widget.chartType === 'invoices_amount_distribution') {
             const labels = data.map(d => this.$t(d.status))
             const values = data.map(d => d.amount)
             widget.chartJsType = 'pie'
             widget.chartData = {
                labels: labels,
                datasets: [{
                    data: values,
                    backgroundColor: ['#198754', '#ffc107', '#0dcaf0', '#dc3545', '#6f42c1', '#fd7e14', '#20c997']
                }]
            }
            widget.chartOptions = {
                legend: { position: 'bottom' }
            }
        }

        if (CURRENCY_CHART_TYPES.has(widget.chartType)) {
          this.applyCurrencyChartFormatting(widget)
        }

        this.$set(widget, 'barCardItems', this.isBarChart(widget) ? this.buildBarCardItems(widget) : [])
    },
    getNestedValue(source, path) {
      if (!path) return source
      return path.split('.').reduce((value, key) => {
        if (value === undefined || value === null) return undefined
        return value[key]
      }, source)
    },
    transformWidgetPayload(widget, payload) {
      if (widget.transform === 'best_seller_profit_comparison' && payload) {
        const bestSeller = payload.best_selling_product || {}
        const mostProfitable = payload.most_profitable_product || {}
        return [
          {
            label: this.$t('best_selling_product'),
            product_name: bestSeller.product_name || '-',
            total_quantity: bestSeller.total_quantity || bestSeller.total_sold || 0,
            profit: bestSeller.profit || ''
          },
          {
            label: this.$t('most_profitable_product'),
            product_name: mostProfitable.product_name || '-',
            total_quantity: mostProfitable.total_quantity || mostProfitable.total_sold || '',
            profit: mostProfitable.profit || 0
          }
        ]
      }
      return payload
    },
    refreshWidgets(options = {}) {
      this.widgets.forEach(w => {
        // Skip text widgets (they don't need to fetch data)
        if (w.type === 'text') {
          w.loading = false
          return
        }
        if (options.dateRangeChanged && !this.widgetUsesDateRange(w)) {
          w.loading = false
          return
        }
        w.loading = true
        this.fetchWidgetData(w)
      })
    },
    onDragEnd() {
      // Optional: Auto-save on drag end
    },
    formatValue(widget) {
        const value = widget.value;
        if (value === undefined || value === null || value === '-') return '-';
        
        if (widget.format === 'currency') {
            return helper.formatMoney(parseFloat(value).toFixed(2));
        }
        
        if (widget.format === 'percentage') {
            return parseFloat(value).toFixed(2) + '%';
        }
        
        return value.toLocaleString();
    },
    handleDateRangeChange() {
      const today = moment()
      
      switch(this.dateRangeOption) {
        case 'today':
          this.start = today.format('YYYY-MM-DD')
          this.end = today.format('YYYY-MM-DD')
          break
        case 'yesterday':
          this.start = moment().subtract(1, 'days').format('YYYY-MM-DD')
          this.end = moment().subtract(1, 'days').format('YYYY-MM-DD')
          break
        case 'this_week':
          this.start = moment().startOf('week').format('YYYY-MM-DD')
          this.end = moment().endOf('week').format('YYYY-MM-DD')
          break
        case 'last_week':
          this.start = moment().subtract(1, 'week').startOf('week').format('YYYY-MM-DD')
          this.end = moment().subtract(1, 'week').endOf('week').format('YYYY-MM-DD')
          break
        case 'this_month':
          this.start = moment().startOf('month').format('YYYY-MM-DD')
          this.end = moment().endOf('month').format('YYYY-MM-DD')
          break
        case 'last_month':
          this.start = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
          this.end = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
          break
        case 'this_quarter':
          this.start = moment().startOf('quarter').format('YYYY-MM-DD')
          this.end = moment().endOf('quarter').format('YYYY-MM-DD')
          break
        case 'this_year':
          this.start = moment().startOf('year').format('YYYY-MM-DD')
          this.end = moment().endOf('year').format('YYYY-MM-DD')
          break
        case 'last_year':
          this.start = moment().subtract(1, 'year').startOf('year').format('YYYY-MM-DD')
          this.end = moment().subtract(1, 'year').endOf('year').format('YYYY-MM-DD')
          break
        case 'custom':
          // Will be handled by range watcher
          return
        default:
          this.start = moment().subtract(3, 'month').format('YYYY-MM-DD')
          this.end = moment().format('YYYY-MM-DD')
      }
      
      this.refreshWidgets({ dateRangeChanged: true })
    },
    
    async fetchUsers() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/users?per_page=5`)
        if (response && response.data && response.data.data) {
          this.users = response.data.data
        }
      } catch (e) {
        alert(e)
      }
    },
    
    async fetchWarehouses() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/warehouses?per_page=1000`)
        if (response && response.data && response.data.data) {
          this.warehouses = response.data.data
        }
      } catch (e) {
        alert(e)
      }
    },
    async handleProductSearchEvent(data) {
        if (!data || data.clear_input) {
            const widgetIndex = data.index;
            const widget = this.widgets[widgetIndex];
            this.$set(widget, 'productName', '');
            this.$set(widget, 'searchResult', null);
            return;
        }
        
        if (!data.result || !data.result.product) return;
        
        const widgetIndex = data.index;
        const widget = this.widgets[widgetIndex];
        const productId = data.result.product.id;
        
        this.$set(widget, 'productName', data.result.product.label);
        this.$set(widget, 'dataLoading', true);
        
        try {
            const filterParams = new URLSearchParams()
            if (this.start && this.end) {
                filterParams.append('start', this.start)
                filterParams.append('end', this.end)
            }
            if (this.selectedUserId) {
                filterParams.append('user_id', this.selectedUserId)
            }
            if (this.selectedWarehouseId) {
                filterParams.append('warehouse_id', this.selectedWarehouseId)
            }
            
            filterParams.append('product_id', productId)
            
            const separator = widget.endpoint.includes('?') ? '&' : '?'
            const url = `${process.env.VUE_APP_API}${widget.endpoint}${separator}${filterParams.toString()}`
            const res = await helper.customGet(url)
            
            this.$set(widget, 'searchResult', res.data.data || res.data)
        } catch (e) {
          alert(e)
        } finally {
            this.$set(widget, 'dataLoading', false);
        }
    }
  },
  watch: {
    range(newRange) {
      if (this.dateRangeOption === 'custom' && newRange && newRange.length === 2) {
        this.start = moment(newRange[0]).format('YYYY-MM-DD')
        this.end = moment(newRange[1]).format('YYYY-MM-DD')
        this.refreshWidgets({ dateRangeChanged: true })
      }
    }
  }
}
</script>

<style scoped>
/* Premium Header Styles */
.insights-header {
  border-radius: 12px;
  padding: 1.5rem .5rem;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #0d6efd 0%, #6610f2 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
}

/* Filters Bar */
.filters-bar {
  border-radius: 8px;
  padding: 0.75rem 1rem;
}

.filter-group select {
  border-radius: 8px;
  border-color: #e9ecef;
}

/* Widget Cards */
.draggable-item {
  position: relative;
}

.widget-card {
  cursor: grab;
  position: relative;
  border-radius: 12px !important;
  overflow: visible;
}

.widget-card:active {
  cursor: grabbing;
}

.widget-card:hover .widget-menu-btn {
  opacity: 1;
}

.widget-menu-btn {
  opacity: 0.5;
  transition: opacity 0.2s;
}

.draggable-item:hover,
.draggable-item:focus-within {
  z-index: 20;
}

.insight-help-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: #8a94a6;
  font-size: 0.78rem;
  line-height: 1;
  cursor: help;
}

.insight-help-button:hover,
.insight-help-button:focus {
  color: #0d6efd;
  outline: none;
}

.insight-help-tooltip {
  position: absolute;
  z-index: 1080;
  top: calc(100% + 0.55rem);
  left: 50%;
  width: 260px;
  max-width: 70vw;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  background: #172033;
  color: #fff;
  box-shadow: 0 0.5rem 1.25rem rgba(23, 32, 51, 0.22);
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.45;
  text-align: left;
  text-transform: none;
  white-space: normal;
  visibility: hidden;
  opacity: 0;
  pointer-events: none;
  transform: translate(-50%, -4px);
  transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s ease;
}

.insight-help-button:hover .insight-help-tooltip,
.insight-help-button:focus .insight-help-tooltip,
.insight-help-button:focus-visible .insight-help-tooltip {
  visibility: visible;
  opacity: 1;
  transform: translate(-50%, 0);
}

.add-widget-tile {
  width: 100%;
  min-height: 170px;
  height: 100%;
  border: 1px dashed #cfd7e6;
  border-radius: 12px;
  background: #fff;
  color: #5b6b82;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: border-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.add-widget-tile:hover,
.add-widget-tile:focus {
  border-color: #0d6efd;
  color: #0d6efd;
  box-shadow: 0 0.5rem 1.25rem rgba(13, 110, 253, 0.12);
  transform: translateY(-1px);
  outline: none;
}

.add-widget-tile-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(13, 110, 253, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.add-widget-tile-label {
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
}

/* Widget Icon */
.widget-icon {
  width: 32px;
  height: 32px;
  background: rgba(13, 110, 253, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0d6efd;
  font-size: 0.9rem;
}

/* Widget Loading */
.widget-loading {
  padding: 2rem;
}

/* Number Widget */
.number-widget h2 {
  letter-spacing: -0.02em;
}

.payment-sales-summary-grid {
  display: grid;
  gap: 0.65rem;
  grid-template-columns: repeat(auto-fit, minmax(135px, 1fr));
}

.payment-sales-summary-period {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #edf0f5;
  border-radius: 10px;
  display: flex;
  gap: 0.65rem;
  min-height: 82px;
  padding: 0.75rem;
  text-align: start;
}

.payment-sales-summary-period small,
.payment-sales-summary-period strong {
  display: block;
}

.payment-sales-summary-period small {
  color: #6c757d;
  font-size: 0.67rem;
  font-weight: 700;
  line-height: 1.3;
}

.payment-sales-summary-period strong {
  color: #1f2937;
  font-size: 0.95rem;
  line-height: 1.35;
  margin-top: 0.2rem;
}

.payment-sales-summary-icon {
  align-items: center;
  background: rgba(25, 135, 84, 0.1);
  border-radius: 9px;
  color: #198754;
  display: inline-flex;
  flex: 0 0 36px;
  height: 36px;
  justify-content: center;
}

.payment-sales-summary-period--week .payment-sales-summary-icon {
  background: rgba(13, 110, 253, 0.1);
  color: #0d6efd;
}

.payment-sales-summary-period--month .payment-sales-summary-icon {
  background: rgba(111, 66, 193, 0.1);
  color: #6f42c1;
}

.payment-sales-summary-scope {
  color: #6c757d;
  font-size: 0.72rem;
  margin-top: 0.75rem;
}

.sales-revenue-total {
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #edf0f5;
}

.sales-revenue-total small,
.sales-revenue-status small {
  font-size: 0.67rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.sales-revenue-status {
  min-height: 70px;
  padding: 0.65rem 0.45rem;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
}

.sales-revenue-status strong {
  max-width: 100%;
  font-size: 0.9rem;
  overflow-wrap: anywhere;
}

.sales-revenue-status-paid {
  color: #198754;
  background: rgba(25, 135, 84, 0.1);
}

.sales-revenue-status-pending {
  color: #997404;
  background: rgba(255, 193, 7, 0.16);
}

.sales-revenue-status-canceled {
  color: #dc3545;
  background: rgba(220, 53, 69, 0.1);
}

.trend-indicator {
  position: absolute;
  top: 0.75rem;
  right: 2.75rem;
  font-size: 0.85rem;
  line-height: 1;
}

/* Chart Widget */
.chart-widget {
  padding: 0.5rem;
}

.financial-category-breakdown {
  width: 100%;
  padding: 0.125rem;
  text-align: start;
}

.financial-category-list {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  max-height: 280px;
  overflow-y: auto;
  padding-inline-end: 0.2rem;
}

.financial-category-row {
  border: 1px solid #e7ebf0;
  border-radius: 9px;
  background: #fff;
  padding: 0.5rem 0.65rem 0.45rem;
}

.financial-category-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.financial-category-rank {
  display: inline-flex;
  flex: 0 0 24px;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 7px;
  font-size: 0.7rem;
  font-weight: 800;
}

.financial-category-name {
  flex: 1 1 auto;
  min-width: 0;
  color: #344054;
  font-size: 0.82rem;
  font-weight: 650;
  overflow-wrap: anywhere;
}

.financial-category-amount {
  flex: 0 0 auto;
  font-size: 0.8rem;
  white-space: nowrap;
}

.financial-category-track {
  height: 3px;
  margin-top: 0.4rem;
  margin-inline-start: 2rem;
  overflow: hidden;
  border-radius: 999px;
  background: #edf0f4;
}

.financial-category-track span {
  display: block;
  height: 100%;
  border-radius: inherit;
}

.financial-category-breakdown.is-income .financial-category-rank {
  background: rgba(25, 135, 84, 0.12);
  color: #146c43;
}

.financial-category-breakdown.is-income .financial-category-amount {
  color: #146c43;
}

.financial-category-breakdown.is-income .financial-category-track span {
  background: linear-gradient(90deg, #198754, #20c997);
}

.financial-category-breakdown.is-expense .financial-category-rank {
  background: rgba(220, 53, 69, 0.1);
  color: #b02a37;
}

.financial-category-breakdown.is-expense .financial-category-amount {
  color: #b02a37;
}

.financial-category-breakdown.is-expense .financial-category-track span {
  background: linear-gradient(90deg, #dc3545, #fd7e14);
}

.bar-chart-breakdown .financial-category-rank {
  background: #f2f4f7;
}

.bar-chart-breakdown .financial-category-list {
  gap: 0.42rem;
}

.bar-chart-breakdown .financial-category-row {
  padding: 0.5rem 0.65rem 0.45rem;
}

.bar-chart-breakdown .financial-category-content {
  gap: 0.5rem;
}

.bar-chart-breakdown .financial-category-rank {
  flex-basis: 24px;
  width: 24px;
  height: 24px;
  border-radius: 7px;
  font-size: 0.7rem;
}

.bar-chart-breakdown .financial-category-name {
  font-size: 0.82rem;
}

.bar-chart-breakdown .financial-category-amount {
  font-size: 0.8rem;
}

.bar-chart-breakdown .financial-category-track {
  height: 3px;
  margin-top: 0.4rem;
  margin-inline-start: 2rem;
}

.financial-category-empty {
  display: flex;
  min-height: 150px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  color: #98a2b3;
  font-size: 0.85rem;
}

.financial-category-empty i {
  font-size: 1.4rem;
}

/* Table Widget */
.table-widget table {
  font-size: 0.85rem;
}

.table-widget thead th {
  border-top: none;
  font-weight: 600;
}

/* Gauge Widget */
.gauge-widget {
  padding: 1rem 0;
}

.gauge-container {
  position: relative;
  width: 140px;
  margin: 0 auto;
}

.gauge-svg {
  width: 100%;
  transform: rotate(0deg);
}

.gauge-value {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
}

/* Comparison Widget */
.comparison-widget {
  padding: 1rem 0;
}

.comparison-arrow {
  animation: pulse 2s infinite;
}

/* Add Widget Catalog */
.add-widget-modal-dialog {
  max-width: 1080px;
}

.widget-catalog {
  display: flex;
  height: min(70vh, 620px);
  min-height: 480px;
}

.widget-module-sidebar {
  background: #f8fafc;
  border-inline-end: 1px solid #e7ebf0;
  flex: 0 0 230px;
  overflow: hidden;
  padding: 1rem 0.75rem;
}

.widget-module-sidebar-title {
  color: #8a94a4;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  padding: 0.25rem 0.75rem 0.75rem;
  text-transform: uppercase;
}

.widget-module-nav {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  height: calc(100% - 2rem);
  overflow-y: auto;
}

.widget-module-button {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 9px;
  color: #5f6b7a;
  display: flex;
  flex: 0 0 auto;
  font-size: 0.85rem;
  font-weight: 600;
  gap: 0.65rem;
  min-height: 44px;
  padding: 0.4rem 0.55rem;
  text-align: start;
  transition: background-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;
  width: 100%;
}

.widget-module-button:hover {
  background: #edf3fb;
  color: #0d6efd;
}

.widget-module-button:focus-visible {
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.18);
  outline: none;
}

.widget-module-button.active {
  background: #fff;
  box-shadow: 0 2px 9px rgba(32, 56, 85, 0.09);
  color: #0d6efd;
}

.widget-module-icon {
  align-items: center;
  background: rgba(95, 107, 122, 0.08);
  border-radius: 8px;
  display: inline-flex;
  flex: 0 0 34px;
  height: 34px;
  justify-content: center;
  transition: background-color 0.18s ease;
}

.widget-module-button.active .widget-module-icon {
  background: rgba(13, 110, 253, 0.12);
}

.widget-module-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.widget-module-count {
  background: #e9eef5;
  border-radius: 999px;
  color: #6c7787;
  flex: 0 0 auto;
  font-size: 0.68rem;
  font-weight: 800;
  min-width: 25px;
  padding: 0.18rem 0.42rem;
  text-align: center;
}

.widget-module-button.active .widget-module-count {
  background: rgba(13, 110, 253, 0.12);
  color: #0d6efd;
}

.widget-catalog-content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}

.widget-catalog-toolbar {
  border-bottom: 1px solid #edf0f4;
  flex: 0 0 auto;
  padding: 1rem 1.25rem;
}

.widget-search {
  position: relative;
}

.widget-search > .bi-search {
  color: #8a94a4;
  inset-inline-start: 0.9rem;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
}

.widget-search .form-control {
  background: #f8fafc;
  border-color: #e3e8ef;
  border-radius: 9px;
  height: 42px;
  padding-inline: 2.55rem;
}

.widget-search .form-control:focus {
  background: #fff;
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.12);
}

.widget-search-clear {
  align-items: center;
  background: transparent;
  border: 0;
  color: #8a94a4;
  display: flex;
  height: 32px;
  inset-inline-end: 0.35rem;
  justify-content: center;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  z-index: 2;
}

.widget-search-clear:hover {
  color: #212529;
}

.widget-catalog-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.beginner-recommendations {
  background: linear-gradient(135deg, #f7fbff 0%, #f9f8ff 100%);
  border-bottom: 1px solid #e4eaf2;
  padding: 1rem 1.25rem 1.25rem;
}

.beginner-recommendations-heading {
  align-items: flex-start;
  display: flex;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
}

.beginner-recommendations-heading h6 {
  color: #243247;
  font-size: 0.9rem;
  font-weight: 800;
}

.beginner-recommendations-heading p {
  color: #66758a;
  font-size: 0.78rem;
  line-height: 1.45;
}

.beginner-recommendations-icon {
  align-items: center;
  background: #fff3cd;
  border-radius: 9px;
  color: #997404;
  display: flex;
  flex: 0 0 36px;
  height: 36px;
  justify-content: center;
}

.beginner-recommendations-grid {
  display: grid;
  gap: 0.65rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.beginner-recommendation-card {
  align-items: center;
  background: #fff;
  border: 1px solid #dde5ef;
  border-radius: 11px;
  color: #212529;
  display: flex;
  gap: 0.7rem;
  min-height: 82px;
  padding: 0.75rem;
  text-align: start;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.beginner-recommendation-card:hover {
  border-color: #9ec5fe;
  box-shadow: 0 5px 14px rgba(32, 75, 130, 0.09);
  transform: translateY(-1px);
}

.beginner-recommendation-card:focus-visible {
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.18);
  outline: none;
}

.beginner-recommendation-card strong {
  display: block;
  font-size: 0.82rem;
  margin-bottom: 0.2rem;
}

.beginner-recommendation-card:hover .widget-option-add {
  background: #0d6efd;
  border-color: #0d6efd;
  color: #fff;
}

.widget-module-heading {
  align-items: center;
  background: #f8fafc;
  color: #687386;
  display: flex;
  font-size: 0.72rem;
  font-weight: 800;
  justify-content: space-between;
  letter-spacing: 0.05em;
  padding: 0.6rem 1.25rem;
  position: sticky;
  text-transform: uppercase;
  top: 0;
  z-index: 2;
}

.widget-module-heading span {
  background: #e9eef5;
  border-radius: 999px;
  font-size: 0.65rem;
  letter-spacing: 0;
  min-width: 23px;
  padding: 0.14rem 0.4rem;
  text-align: center;
}

.widget-option {
  border-inline: 0;
  gap: 0.85rem;
  min-height: 76px;
  padding: 0.85rem 1.25rem;
  text-align: start;
}

.widget-option-icon {
  align-items: center;
  background: rgba(13, 110, 253, 0.09);
  border-radius: 10px;
  color: #0d6efd;
  display: flex;
  flex: 0 0 42px;
  font-size: 1.05rem;
  height: 42px;
  justify-content: center;
}

.widget-option-copy {
  min-width: 0;
  text-align: start;
}

.widget-option-copy small {
  display: block;
  line-height: 1.35;
}

.widget-option-add {
  align-items: center;
  border: 1px solid #dce3ec;
  border-radius: 50%;
  color: #6c7787;
  display: flex;
  flex: 0 0 32px;
  height: 32px;
  justify-content: center;
  transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

.widget-option:hover .widget-option-add {
  background: #0d6efd;
  border-color: #0d6efd;
  color: #fff;
}

.widget-catalog-empty {
  padding: 4rem 1rem;
}

.widget-catalog-empty > i {
  display: block;
  font-size: 2rem;
  margin-bottom: 0.75rem;
}

.locked-widget-option {
  cursor: not-allowed;
  opacity: 0.62;
}

.locked-widget-option:hover {
  background: #fff;
}

.locked-widget-badge {
  align-items: center;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  color: #1d4ed8;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 0.7rem;
  font-weight: 800;
  padding: 4px 8px;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

/* Empty State */
.empty-state-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
}

.empty-state-card {
  background: white;
  border-radius: 16px;
  padding: 3rem;
  text-align: center;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  max-width: 500px;
  width: 100%;
}

.empty-state-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #e9ecef 0%, #f8f9fa 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  font-size: 2.5rem;
  color: #6c757d;
}

.preset-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
  margin-top: 1rem;
}

.preset-item {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1rem 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.preset-item:hover {
  background: #e9ecef;
  transform: translateY(-2px);
}

.preset-item.disabled {
  cursor: not-allowed;
  filter: grayscale(1);
  opacity: 0.48;
}

.preset-item.disabled:hover {
  background: #f8f9fa;
  transform: none;
}

.preset-item i {
  font-size: 1.5rem;
}

.preset-item span {
  font-size: 0.75rem;
  font-weight: 500;
  color: #495057;
}

/* Ghost Card for Drag */
.ghost-card {
  opacity: 0.5;
  background: #f0f6ff;
  border: 2px dashed #0d6efd !important;
  border-radius: 12px;
}

/* Module-based widget colors */
.widget-sales { border-left: 4px solid #198754 !important; }
.widget-inventory { border-left: 4px solid #0d6efd !important; }
.widget-financial { border-left: 4px solid #ffc107 !important; }
.widget-partners { border-left: 4px solid #0dcaf0 !important; }
.widget-purchases { border-left: 4px solid #6f42c1 !important; }
.widget-logistics { border-left: 4px solid #fd7e14 !important; }
.widget-pos { border-left: 4px solid #20c997 !important; }
.widget-hr { border-left: 4px solid #0dcaf0 !important; }
.widget-invoice_status { border-left: 4px solid #adb5bd !important; }

/* Responsive */
@media (max-width: 768px) {
  .add-widget-modal-dialog {
    margin: 0.5rem;
    max-width: none;
  }

  .widget-catalog {
    flex-direction: column;
    height: calc(100vh - 130px);
    min-height: 0;
  }

  .widget-module-sidebar {
    border-bottom: 1px solid #e7ebf0;
    border-inline-end: 0;
    flex: 0 0 auto;
    padding: 0.6rem 0.75rem;
    width: auto;
  }

  .widget-module-sidebar-title {
    display: none;
  }

  .widget-module-nav {
    flex-direction: row;
    height: auto;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 0.1rem;
  }

  .widget-module-button {
    min-width: max-content;
    padding-inline: 0.65rem;
    width: auto;
  }

  .widget-module-name {
    overflow: visible;
  }

  .widget-catalog-content {
    min-height: 0;
  }

  .widget-catalog-toolbar {
    padding: 0.75rem;
  }

  .widget-option {
    padding-inline: 0.85rem;
  }

  .widget-option-copy small {
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  .beginner-recommendations {
    padding: 0.85rem;
  }

  .beginner-recommendations-grid {
    grid-template-columns: 1fr;
  }

  .preset-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .insights-header {
    padding: 1rem;
  }
  
  .header-icon {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }
}

/* Floating Save Bar */
.floating-save-bar {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1050;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  padding: 12px 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.08);
}

.floating-save-content {
  display: flex;
  align-items: center;
  gap: 20px;
}

.floating-save-hint {
  font-size: 0.85rem;
  font-weight: 600;
  color: #6c757d;
  white-space: nowrap;
}

/* Fade-up transition */
.fade-up-enter-active,
.fade-up-leave-active {
  transition: all 0.3s ease;
}
.fade-up-enter,
.fade-up-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}

@media (max-width: 768px) {
  .floating-save-bar {
    left: 16px;
    right: 16px;
    transform: none;
    bottom: 16px;
  }
  .fade-up-enter,
  .fade-up-leave-to {
    transform: translateY(20px);
  }
}

/* Dashboard Tabs Bar Styling */
.dashboard-tabs-bar {
  margin-top: 1rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0px;
}

.tabs-scroll-container {
  overflow-x: auto;
  white-space: nowrap;
  scrollbar-width: none; /* Firefox */
  padding-bottom: 120px;
  margin-bottom: -120px;
}

.tabs-scroll-container::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.dashboard-tabs {
  display: flex;
  align-items: flex-end;
  gap: 6px;
}

.dashboard-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-bottom: none;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  position: relative;
  user-select: none;
  z-index: 1;
}

.dashboard-tab:hover,
.dashboard-tab:focus-within {
  background-color: #e9ecef;
  color: #0b5ed7;
  z-index: 10;
}

.dashboard-tab.active {
  background-color: #ffffff;
  border-color: #e9ecef;
  border-bottom: 2px solid #ffffff;
  margin-bottom: -2px;
  z-index: 5;
  font-weight: 600;
  color: #0d6efd;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
}

.dashboard-tab.active:hover,
.dashboard-tab.active:focus-within {
  z-index: 12;
}

.tab-icon {
  font-size: 1rem;
}

.tab-label {
  font-size: 0.9rem;
}

.tab-actions {
  margin-left: 6px;
  display: inline-flex;
  align-items: center;
  position: relative;
  z-index: 20;
}

.tab-actions .dropdown-menu {
  z-index: 1050 !important;
}

.tab-menu-btn {
  color: #6c757d;
  padding: 0;
  line-height: 1;
}

.tab-menu-btn:hover {
  color: #212529;
}

.add-tab-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 50%;
  margin-left: 8px;
  margin-bottom: 2px;
  cursor: pointer;
  color: #6c757d;
  transition: all 0.2s ease;
}

.add-tab-btn:hover {
  background-color: #e9ecef;
  color: #212529;
  transform: scale(1.05);
}

/* Icon Picker in Modals */
.tab-icon-picker {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 6px;
  max-height: 150px;
  overflow-y: auto;
  padding: 8px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background-color: #f8f9fa;
}

.icon-option {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  border: 1px solid transparent;
  background: white;
  border-radius: 4px;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 1rem;
}

.icon-option:hover {
  background-color: #e9ecef;
  color: #0d6efd;
}

.icon-option.selected {
  background-color: #0d6efd;
  color: white;
  border-color: #0d6efd;
}

@keyframes pulse-green {
  0% {
    box-shadow: 0 0 0 0 rgba(25, 135, 84, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(25, 135, 84, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 135, 84, 0);
  }
}

.animation-pulse-green {
  animation: pulse-green 2s infinite;
}

</style>
